import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AccountCircle } from '@mui/icons-material';
import axiosInstance from '../../../config/axiosInstance';
import { useAuth } from '../../../context/AuthContext';
import {
  copyPokerStudyPreflopRangeTemplate,
  createPokerStudyPreflopRangeTemplate,
  createPokerStudySpot,
  deletePokerStudyPreflopRangeTemplate,
  deletePokerStudySpot,
  listPokerStudyBoards,
  listPokerStudyPreflopRangeTemplates,
  listPokerStudySpots,
  updatePokerStudyPreflopRangeTemplate,
  updatePokerStudySpot,
} from '../services/pokerStudyService';
import type {
  CopyPokerStudyPreflopRangeTemplatePayload,
  CreatePokerStudyPreflopRangeTemplatePayload,
  CreatePokerStudySpotPayload,
  PokerStudyBoardListItem,
  PokerStudyPreflopRangeTemplate,
  PokerStudySpotListItem,
} from '../types/pokerStudy';

type PokerStudyMainSection = 'spots' | 'boards' | 'ranges';

type SpotDialogMode = 'create' | 'edit';

type PokerStudyTopNavItemProps = {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
};

function PokerStudyTopNavItem({
  label,
  active,
  disabled = false,
  onClick,
}: PokerStudyTopNavItemProps) {
  return (
    <Button
      variant="outlined"
      disabled={disabled}
      onClick={onClick}
      sx={{
        minHeight: 38,
        borderRadius: 2.25,
        px: 1.8,
        textTransform: 'none',
        fontWeight: active ? 950 : 850,
        borderColor: active ? alpha('#38bdf8', 0.42) : alpha('#94a3b8', 0.16),
        bgcolor: active ? alpha('#38bdf8', 0.12) : 'transparent',
        color: active ? '#f8fafc' : '#94a3b8',
        opacity: disabled ? 0.46 : 1,
        transition: 'all 0.18s ease',
        '&:hover': {
          bgcolor: active ? alpha('#38bdf8', 0.15) : alpha('#ffffff', 0.035),
          borderColor: alpha('#38bdf8', 0.36),
          color: '#e2e8f0',
        },
      }}
    >
      {label}
    </Button>
  );
}

type PokerStudyTopNavProps = {
  activeSection: PokerStudyMainSection;
  activeUserEmail: string;
  onChangeSection: (section: PokerStudyMainSection) => void;
  onRequestPasswordChange: () => void;
  onSignOut: () => void;
};

function PokerStudyTopNav({
  activeSection,
  activeUserEmail,
  onChangeSection,
  onRequestPasswordChange,
  onSignOut,
}: PokerStudyTopNavProps) {
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isUserMenuOpen = Boolean(userMenuAnchorEl);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        borderBottom: `1px solid ${alpha('#94a3b8', 0.16)}`,
        bgcolor: 'rgba(8,18,36,0.94)',
        backdropFilter: 'blur(14px)',
        px: { xs: 2, md: 3, xl: 4 },
        py: 1.25,
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.75}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="PROA Poker Study"
            sx={{
              height: 30,
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0,
              filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.3))',
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
                letterSpacing: '-0.6px',
                fontSize: '1.18rem',
                lineHeight: 1,
              }}
            >
              PROAPokerStudy
            </Typography>

            <Typography
              sx={{
                color: '#64748b',
                fontWeight: 800,
                letterSpacing: 0.8,
                fontSize: '0.68rem',
                lineHeight: 1,
                textTransform: 'uppercase',
                mt: 0.55,
              }}
            >
              Poker Study System
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={0.85}
          alignItems="center"
          justifyContent="center"
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <PokerStudyTopNavItem
            label="Spots"
            active={activeSection === 'spots'}
            onClick={() => onChangeSection('spots')}
          />

          <PokerStudyTopNavItem
            label="Boards"
            active={activeSection === 'boards'}
            onClick={() => onChangeSection('boards')}
          />

          <PokerStudyTopNavItem
            label="Rangos"
            active={activeSection === 'ranges'}
            onClick={() => onChangeSection('ranges')}
          />
        </Stack>

        <Box>
          <Button
            variant="outlined"
            onClick={(event) => setUserMenuAnchorEl(event.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.15,
              px: 1.25,
              py: 0.9,
              borderRadius: 2.5,
              bgcolor: alpha('#ffffff', 0.025),
              borderColor: alpha('#94a3b8', 0.1),
              minWidth: { xs: '100%', lg: 230 },
              justifyContent: { xs: 'flex-start', lg: 'flex-end' },
              textTransform: 'none',
              color: '#f8fafc',
              '&:hover': {
                bgcolor: alpha('#38bdf8', 0.08),
                borderColor: alpha('#38bdf8', 0.34),
              },
            }}
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen ? 'true' : undefined}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha('#38bdf8', 0.1),
                color: '#38bdf8',
                border: `1px solid ${alpha('#38bdf8', 0.3)}`,
                flexShrink: 0,
              }}
            >
              <AccountCircle fontSize="medium" />
            </Avatar>

            <Box sx={{ minWidth: 0, textAlign: 'left' }}>
              <Typography
                noWrap
                sx={{
                  color: '#f8fafc',
                  fontWeight: 850,
                  fontSize: '0.9rem',
                  lineHeight: 1.2,
                }}
              >
                Usuario
              </Typography>
              <Typography
                noWrap
                title={activeUserEmail}
                sx={{
                  color: '#64748b',
                  fontWeight: 700,
                  fontSize: '0.76rem',
                  lineHeight: 1.2,
                  maxWidth: { xs: 230, lg: 280 },
                }}
              >
                {activeUserEmail}
              </Typography>
            </Box>
          </Button>

          <Menu
            anchorEl={userMenuAnchorEl}
            open={isUserMenuOpen}
            onClose={() => setUserMenuAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 230,
                borderRadius: 2.5,
                bgcolor: '#1e293b',
                border: '1px solid rgba(148, 163, 184, 0.22)',
                boxShadow: '0 22px 60px rgba(0,0,0,0.45)',
                color: '#f8fafc',
              },
            }}
          >
            <Box sx={{ px: 1.6, py: 1.2 }}>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: 11,
                  fontWeight: 950,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                }}
              >
                Sesión activa
              </Typography>
              <Typography
                noWrap
                title={activeUserEmail}
                sx={{
                  mt: 0.35,
                  color: '#f8fafc',
                  fontSize: 13,
                  fontWeight: 850,
                  maxWidth: 220,
                }}
              >
                {activeUserEmail}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.16)' }} />

            <MenuItem
              onClick={() => {
                setUserMenuAnchorEl(null);
                onRequestPasswordChange();
              }}
              sx={{
                color: '#cbd5e1',
                fontWeight: 850,
                fontSize: 13.5,
                py: 1.2,
                '&:hover': {
                  bgcolor: 'rgba(56, 189, 248, 0.08)',
                  color: '#38bdf8',
                },
              }}
            >
              Cambiar contraseña
            </MenuItem>

            <MenuItem
              onClick={() => {
                setUserMenuAnchorEl(null);
                onSignOut();
              }}
              sx={{
                color: '#f87171',
                fontWeight: 850,
                fontSize: 13.5,
                py: 1.2,
                '&:hover': {
                  bgcolor: 'rgba(248, 113, 113, 0.08)',
                },
              }}
            >
              Cerrar sesión
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
    </Box>
  );
}

type RangeTemplateDialogMode = 'create' | 'edit';

type RangeTemplateFormState = {
  name: string;
  range_family: string;
  position: string;
  versus_position: string;
  spot_type: string;
  opponent_profile: string;
  description: string;
  tags_text: string;
};

const INITIAL_RANGE_TEMPLATE_FORM: RangeTemplateFormState = {
  name: '',
  range_family: 'open_raise',
  position: 'co',
  versus_position: 'none',
  spot_type: 'none',
  opponent_profile: 'none',
  description: '',
  tags_text: '',
};

const RANGE_FAMILY_OPTIONS = [
  { value: 'open_raise', label: 'Open Raise' },
  { value: 'call_vs_open', label: 'Defensa vs Open' },
  { value: 'three_bet', label: '3Bet' },
  { value: 'call_vs_3bet', label: 'Defensa vs 3Bet' },
];

const RANGE_TEMPLATE_OPPONENT_PROFILE_OPTIONS = [
  { value: 'none', label: 'Sin perfil / No aplica' },
  { value: 'regular', label: 'Regular' },
  { value: 'recreational', label: 'Recreacional' },
  { value: 'unknown', label: 'Desconocido' },
];

function normalizeRangeFamily(value: string): string {
  const normalized = value.trim().toLowerCase();

  if (normalized === 'open raise' || normalized === 'or') {
    return 'open_raise';
  }

  if (
    normalized === 'call vs open' ||
    normalized === 'defend_blind' ||
    normalized === 'defensa ciega'
  ) {
    return 'call_vs_open';
  }

  if (normalized === '3bet' || normalized === '3-bet') {
    return 'three_bet';
  }

  if (
    normalized === 'call vs 3bet' ||
    normalized === 'call_vs_three_bet' ||
    normalized === 'defensa vs 3bet'
  ) {
    return 'call_vs_3bet';
  }

  return normalized;
}

function formatRangeFamilyLabel(value: string): string {
  const normalized = normalizeRangeFamily(value);
  const option = RANGE_FAMILY_OPTIONS.find((item) => item.value === normalized);
  return option?.label || value;
}

function formatRangeTemplateOpponentProfileLabel(value: string | null | undefined): string {
  const normalized = (value || '').trim().toLowerCase();
  const option = RANGE_TEMPLATE_OPPONENT_PROFILE_OPTIONS.find(
    (item) => item.value === normalized,
  );

  return option?.label || value || 'Sin perfil / No aplica';
}

function getRangeTemplateSection(template: PokerStudyPreflopRangeTemplate): 'srp' | 'three_bet' {
  const family = normalizeRangeFamily(template.range_family);

  if (family === 'three_bet' || family === 'call_vs_3bet') {
    return 'three_bet';
  }

  return 'srp';
}

function getTemplateHandClasses(template: PokerStudyPreflopRangeTemplate): string[] {
  return Array.isArray(template.range_state?.hand_classes)
    ? template.range_state.hand_classes
    : [];
}

function getTemplateComboCount(template: PokerStudyPreflopRangeTemplate): number {
  return getTemplateHandClasses(template).reduce((acc, handClass) => {
    if (handClass.length === 2) return acc + 6;
    if (handClass.endsWith('s')) return acc + 4;
    if (handClass.endsWith('o')) return acc + 12;
    return acc;
  }, 0);
}

function formatTemplatePercentage(comboCount: number): string {
  if (!comboCount) return '0%';
  return `${((comboCount / 1326) * 100).toFixed(1)}%`;
}

const RANGE_TEMPLATE_MATRIX_RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];

function buildRangeTemplateHandClass(rowRank: string, colRank: string): string {
  if (rowRank === colRank) {
    return `${rowRank}${colRank}`;
  }

  return RANGE_TEMPLATE_MATRIX_RANKS.indexOf(rowRank) <
    RANGE_TEMPLATE_MATRIX_RANKS.indexOf(colRank)
    ? `${rowRank}${colRank}s`
    : `${colRank}${rowRank}o`;
}

function buildRangeTemplateMatrix(): string[][] {
  return RANGE_TEMPLATE_MATRIX_RANKS.map((rowRank) =>
    RANGE_TEMPLATE_MATRIX_RANKS.map((colRank) =>
      buildRangeTemplateHandClass(rowRank, colRank),
    ),
  );
}

function sortRangeTemplateHandClasses(handClasses: string[]): string[] {
  const order = new Map<string, number>();

  buildRangeTemplateMatrix().flat().forEach((handClass, index) => {
    order.set(handClass, index);
  });

  return [...handClasses].sort(
    (left, right) => (order.get(left) ?? 9999) - (order.get(right) ?? 9999),
  );
}

function isRangeTemplatePocketPair(handClass: string): boolean {
  return handClass.length === 2 && handClass[0] === handClass[1];
}

function buildRangeTemplatePayload(
  form: RangeTemplateFormState,
): CreatePokerStudyPreflopRangeTemplatePayload {
  return {
    name: form.name.trim(),
    range_family: normalizeRangeFamily(form.range_family),
    position: form.position.trim().toLowerCase(),
    versus_position:
      form.versus_position === 'none' ? null : form.versus_position.trim().toLowerCase(),
    spot_type: form.spot_type === 'none' ? null : form.spot_type.trim().toLowerCase(),
    opponent_profile:
      form.opponent_profile === 'none' ? null : form.opponent_profile.trim().toLowerCase(),
    description: form.description,
    tags: form.tags_text
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    range_state: {
      version: 1,
      hand_classes: [],
    },
  };
}

function buildRangeTemplateFormFromTemplate(
  template: PokerStudyPreflopRangeTemplate,
): RangeTemplateFormState {
  return {
    name: template.name,
    range_family: template.range_family,
    position: template.position,
    versus_position: template.versus_position || 'none',
    spot_type: template.spot_type || 'none',
    opponent_profile: template.opponent_profile || 'none',
    description: template.description || '',
    tags_text: template.tags.join(', '),
  };
}

type SpotFormState = {
  title: string;
  game_type: string;
  spot_type: string;
  hero_position: string;
  villain_position: string;
  perspective_position: string;
  position_a_profile: string;
  position_b_profile: string;
  open_raise_size_bb: string;
  three_bet_size_bb: string;
  four_bet_size_bb: string;
  rol_size_bb: string;
  effective_stack_bb: string;
  general_tags_text: string;
};

type PreflopActors = {
  firstPosition: string;
  secondPosition: string;
  initiativePosition: string;
  sequenceLabel: string;
};

const SPOT_TYPE_OPTIONS = [
  { value: 'srp', label: 'SRP' },
  { value: '3bet', label: '3Bet pot' },
  { value: '4bet', label: '4Bet pot' },
  { value: 'rol', label: 'ROL' },
];

const POSITION_OPTIONS = ['utg', 'hj', 'co', 'btn', 'sb', 'bb'];

const PLAYER_PROFILE_OPTIONS = [
  { value: 'hero', label: 'Yo' },
  { value: 'regular', label: 'Regular' },
  { value: 'recreational', label: 'Recreacional' },
  { value: 'unknown', label: 'Desconocido' },
];

const PREFLOP_POSITION_ORDER = ['utg', 'hj', 'co', 'btn', 'sb', 'bb'];

function getPreflopPositionOrder(position: string): number {
  const index = PREFLOP_POSITION_ORDER.indexOf(position);
  return index === -1 ? 999 : index;
}

function derivePreflopActors(
  spotType: string,
  positionA: string,
  positionB: string,
): PreflopActors {
  const normalizedSpotType = spotType.trim().toLowerCase();
  const normalizedPositionA = positionA.trim().toLowerCase();
  const normalizedPositionB = positionB.trim().toLowerCase();

  const firstPosition =
    getPreflopPositionOrder(normalizedPositionA) <= getPreflopPositionOrder(normalizedPositionB)
      ? normalizedPositionA
      : normalizedPositionB;

  const secondPosition = firstPosition === normalizedPositionA ? normalizedPositionB : normalizedPositionA;

  if (normalizedSpotType === '3bet') {
    return {
      firstPosition,
      secondPosition,
      initiativePosition: secondPosition,
      sequenceLabel: `${firstPosition.toUpperCase()} OR → ${secondPosition.toUpperCase()} 3Bet → ${firstPosition.toUpperCase()} Call`,
    };
  }

  if (normalizedSpotType === '4bet') {
    return {
      firstPosition,
      secondPosition,
      initiativePosition: firstPosition,
      sequenceLabel: `${firstPosition.toUpperCase()} OR → ${secondPosition.toUpperCase()} 3Bet → ${firstPosition.toUpperCase()} 4Bet → ${secondPosition.toUpperCase()} Call`,
    };
  }

  if (normalizedSpotType === 'rol') {
    return {
      firstPosition,
      secondPosition,
      initiativePosition: secondPosition,
      sequenceLabel: `${firstPosition.toUpperCase()} Limp → ${secondPosition.toUpperCase()} ROL → ${firstPosition.toUpperCase()} Call`,
    };
  }

  return {
    firstPosition,
    secondPosition,
    initiativePosition: firstPosition,
    sequenceLabel: `${firstPosition.toUpperCase()} OR → ${secondPosition.toUpperCase()} Call`,
  };
}

const INITIAL_FORM: SpotFormState = {
  title: '',
  game_type: 'cash',
  spot_type: 'srp',
  hero_position: 'btn',
  villain_position: 'bb',
  perspective_position: 'btn',
  position_a_profile: 'hero',
  position_b_profile: 'regular',
  open_raise_size_bb: '2.5',
  three_bet_size_bb: '10',
  four_bet_size_bb: '22',
  rol_size_bb: '4',
  effective_stack_bb: '100',
  general_tags_text: 'btn_vs_bb, srp',
};

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatSpotTypeLabel(value: string): string {
  const option = SPOT_TYPE_OPTIONS.find((item) => item.value === value.trim().toLowerCase());
  return option?.label || value.toUpperCase();
}

function buildDefaultSpotTitle(spotType: string, preflopActors: PreflopActors): string {
  const initiativePosition = preflopActors.initiativePosition.toUpperCase();
  const otherPosition =
    preflopActors.firstPosition === preflopActors.initiativePosition
      ? preflopActors.secondPosition.toUpperCase()
      : preflopActors.firstPosition.toUpperCase();

  return `${formatSpotTypeLabel(spotType)} - ${initiativePosition} vs ${otherPosition}`;
}

function getSpotTypeChipStyle(spotType: string) {
  switch (spotType.trim().toLowerCase()) {
    case 'srp':
      return {
        label: 'SRP',
        bgcolor: 'rgba(34, 197, 94, 0.18)',
        color: '#86efac',
        borderColor: 'rgba(34, 197, 94, 0.42)',
      };
    case '3bet':
      return {
        label: '3BET',
        bgcolor: 'rgba(59, 130, 246, 0.18)',
        color: '#93c5fd',
        borderColor: 'rgba(59, 130, 246, 0.42)',
      };
    case '4bet':
      return {
        label: '4BET',
        bgcolor: 'rgba(168, 85, 247, 0.18)',
        color: '#d8b4fe',
        borderColor: 'rgba(168, 85, 247, 0.42)',
      };
    case 'rol':
      return {
        label: 'ROL',
        bgcolor: 'rgba(245, 158, 11, 0.18)',
        color: '#fcd34d',
        borderColor: 'rgba(245, 158, 11, 0.42)',
      };
    default:
      return {
        label: spotType.toUpperCase(),
        bgcolor: 'rgba(148, 163, 184, 0.14)',
        color: '#cbd5e1',
        borderColor: 'rgba(148, 163, 184, 0.34)',
      };
  }
}

function buildFormFromSpot(spot: PokerStudySpotListItem): SpotFormState {
  const preflopConfig = spot.preflop_config || {};
  const playerConfig = spot.player_config || {};
  const profiles =
    playerConfig.profiles && typeof playerConfig.profiles === 'object'
      ? (playerConfig.profiles as Record<string, unknown>)
      : {};

  const perspectivePosition =
    typeof playerConfig.perspective_position === 'string'
      ? playerConfig.perspective_position
      : 'neutral';

  const readNumberAsString = (value: unknown, fallback: string) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    if (typeof value === 'string' && value.trim()) {
      return value;
    }

    return fallback;
  };

  return {
    title: '',
    game_type: spot.game_type || 'cash',
    spot_type: spot.spot_type || 'srp',
    hero_position: spot.hero_position || 'btn',
    villain_position: spot.villain_position || 'bb',
    perspective_position: perspectivePosition,
    position_a_profile:
      typeof profiles[spot.hero_position] === 'string'
        ? (profiles[spot.hero_position] as string)
        : 'hero',
    position_b_profile:
      typeof profiles[spot.villain_position] === 'string'
        ? (profiles[spot.villain_position] as string)
        : spot.villain_profile || 'regular',
    open_raise_size_bb: readNumberAsString(preflopConfig.open_raise_size_bb, '2.5'),
    three_bet_size_bb: readNumberAsString(preflopConfig.three_bet_size_bb, '10'),
    four_bet_size_bb: readNumberAsString(preflopConfig.four_bet_size_bb, '22'),
    rol_size_bb: readNumberAsString(preflopConfig.rol_size_bb, '4'),
    effective_stack_bb:
      spot.effective_stack_bb !== null && spot.effective_stack_bb !== undefined
        ? String(spot.effective_stack_bb)
        : '100',
    general_tags_text: spot.general_tags.join(', '),
  };
}

function buildCreatePayload(form: SpotFormState): CreatePokerStudySpotPayload {
  const positionA = form.hero_position.trim().toLowerCase();
  const positionB = form.villain_position.trim().toLowerCase();
  const spotType = form.spot_type.trim().toLowerCase();
  const preflopActors = derivePreflopActors(spotType, positionA, positionB);

  const preflopConfig = {
    spot_type: spotType,
    position_a: positionA,
    position_b: positionB,
    first_position: preflopActors.firstPosition,
    second_position: preflopActors.secondPosition,
    initiative_position: preflopActors.initiativePosition,
    sequence_label: preflopActors.sequenceLabel,
    open_raise_size_bb: parseOptionalNumber(form.open_raise_size_bb),
    three_bet_size_bb: parseOptionalNumber(form.three_bet_size_bb),
    four_bet_size_bb: parseOptionalNumber(form.four_bet_size_bb),
    rol_size_bb: parseOptionalNumber(form.rol_size_bb),
    unit: 'bb',
  };

  const playerConfig = {
    perspective_position:
      form.perspective_position === 'neutral' ? null : form.perspective_position,
    profiles: {
      [positionA]: form.position_a_profile,
      [positionB]: form.position_b_profile,
    },
  };

  return {
    title: form.title.trim() || buildDefaultSpotTitle(spotType, preflopActors),
    game_type: form.game_type.trim().toLowerCase(),
    spot_type: spotType,
    hero_position: positionA,
    villain_position: positionB,
    initiative_role: `${preflopActors.initiativePosition}_initiative`,
    villain_profile: form.position_b_profile,
    effective_stack_bb: parseOptionalNumber(form.effective_stack_bb),
    preflop_config: preflopConfig,
    player_config: playerConfig,
    general_tags: form.general_tags_text
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    is_favorite: false,
  };
}

function formatSpotSubtitle(spot: PokerStudySpotListItem): string {
  const parts = [
    spot.game_type.toUpperCase(),
    `${spot.hero_position.toUpperCase()} vs ${spot.villain_position.toUpperCase()}`,
  ];

  if (spot.effective_stack_bb !== null && spot.effective_stack_bb !== undefined) {
    parts.push(`${spot.effective_stack_bb}bb`);
  }

  return parts.join(' · ');
}

function readStringFromRecord(
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = source?.[key];

  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function formatPlayerProfileLabel(value: string | null | undefined): string {
  switch ((value || '').trim().toLowerCase()) {
    case 'hero':
      return 'Yo';
    case 'regular':
      return 'Regular';
    case 'recreational':
      return 'Recreacional';
    case 'unknown':
      return 'Desconocido';
    default:
      return value || '-';
  }
}

function buildSpotSequenceLabel(spot: PokerStudySpotListItem): string {
  const sequenceLabel = readStringFromRecord(spot.preflop_config, 'sequence_label');

  if (sequenceLabel) {
    return sequenceLabel;
  }

  const actors = derivePreflopActors(
    spot.spot_type,
    spot.hero_position,
    spot.villain_position,
  );

  return actors.sequenceLabel;
}

function readActiveSessionEmail(): string {
  if (typeof window === 'undefined') {
    return 'Sesión activa';
  }

  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);

      if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) {
        continue;
      }

      const rawValue = window.localStorage.getItem(key);

      if (!rawValue) {
        continue;
      }

      const parsed = JSON.parse(rawValue) as {
        user?: {
          email?: unknown;
        };
      };

      const email = parsed.user?.email;

      if (typeof email === 'string' && email.trim()) {
        return email.trim();
      }
    }
  } catch (error) {
    console.error('Poker Study session email read error', error);
  }

  return 'Sesión activa';
}

function buildBoardDisplayLabel(board: PokerStudyBoardListItem): string {
  if (board.board_mode === 'exact_board') {
    return `${board.flop_card_1 ?? ''} ${board.flop_card_2 ?? ''} ${board.flop_card_3 ?? ''}`.trim();
  }

  return board.board_family_label || 'Board family';
}

function formatBoardTextureLabel(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  if (value === 'one') {
    return '1 broadway';
  }

  if (value === 'two') {
    return '2 broadways';
  }

  if (value === 'three') {
    return '3 broadways';
  }

  if (value === 'none') {
    return '0 broadways';
  }

  return value;
}

function formatBoardVillainProfileLabel(value: string | null | undefined): string {
  switch ((value || '').trim().toLowerCase()) {
    case 'regular':
      return 'Regular';
    case 'recreational':
      return 'Recreacional';
    case 'unknown':
      return 'Desconocido';
    default:
      return value || 'Sin perfil';
  }
}

function getBoardBroadwayCount(board: PokerStudyBoardListItem): number | null {
  if (board.broadway_count !== null && board.broadway_count !== undefined) {
    return board.broadway_count;
  }

  switch ((board.broadway_density || '').trim().toLowerCase()) {
    case 'none':
      return 0;
    case 'one':
      return 1;
    case 'two':
      return 2;
    case 'three':
      return 3;
    default:
      return null;
  }
}

function matchesBoardHighCardFilter(
  board: PokerStudyBoardListItem,
  filterValue: string,
): boolean {
  if (filterValue === 'all') return true;

  const highCardValue = board.high_card_value;

  if (highCardValue === null || highCardValue === undefined) {
    return false;
  }

  if (filterValue === '9_or_lower') {
    return highCardValue <= 9;
  }

  return highCardValue === Number(filterValue);
}

function matchesBoardBooleanFilter(value: boolean | null | undefined, filterValue: string): boolean {
  if (filterValue === 'all') return true;
  if (filterValue === 'yes') return Boolean(value);
  if (filterValue === 'no') return !value;

  return true;
}

const BOARD_CARD_SUIT_META: Record<string, { symbol: string; color: string }> = {
  s: { symbol: '♠', color: '#111827' },
  h: { symbol: '♥', color: '#ef4444' },
  d: { symbol: '♦', color: '#2563eb' },
  c: { symbol: '♣', color: '#16a34a' },
};

function parseBoardCardToken(value: string | null | undefined): { rank: string; suit: string } | null {
  const token = (value || '').trim();

  if (token.length !== 2) {
    return null;
  }

  return {
    rank: token[0].toUpperCase(),
    suit: token[1].toLowerCase(),
  };
}

function PokerStudyBoardMiniCard({ value }: { value: string | null | undefined }) {
  const parsed = parseBoardCardToken(value);
  const suit = parsed ? BOARD_CARD_SUIT_META[parsed.suit] : null;

  return (
    <Box
      sx={{
        width: 34,
        minWidth: 34,
        height: 44,
        borderRadius: 1.25,
        bgcolor: suit?.color || 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.28)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}
    >
      <Typography
        component="span"
        sx={{
          color: '#ffffff',
          fontSize: 16,
          fontWeight: 950,
          lineHeight: 0.95,
        }}
      >
        {parsed?.rank || '?'}
      </Typography>
      <Typography
        component="span"
        sx={{
          color: '#ffffff',
          fontSize: 11.5,
          fontWeight: 950,
          lineHeight: 0.95,
        }}
      >
        {suit?.symbol || '?'}
      </Typography>
    </Box>
  );
}

function getBoardSpotTypeChipStyle(spotType: string) {
  switch (spotType.trim().toLowerCase()) {
    case 'srp':
      return {
        label: 'SRP',
        color: '#86efac',
        bgcolor: 'rgba(34, 197, 94, 0.14)',
        borderColor: 'rgba(34, 197, 94, 0.38)',
      };
    case '3bet':
      return {
        label: '3BET',
        color: '#93c5fd',
        bgcolor: 'rgba(59, 130, 246, 0.14)',
        borderColor: 'rgba(59, 130, 246, 0.38)',
      };
    case '4bet':
      return {
        label: '4BET',
        color: '#d8b4fe',
        bgcolor: 'rgba(168, 85, 247, 0.14)',
        borderColor: 'rgba(168, 85, 247, 0.38)',
      };
    case 'rol':
      return {
        label: 'ROL',
        color: '#fcd34d',
        bgcolor: 'rgba(245, 158, 11, 0.14)',
        borderColor: 'rgba(245, 158, 11, 0.38)',
      };
    default:
      return {
        label: spotType.toUpperCase(),
        color: '#cbd5e1',
        bgcolor: 'rgba(148, 163, 184, 0.12)',
        borderColor: 'rgba(148, 163, 184, 0.28)',
      };
  }
}

type PokerStudyBoardsSectionProps = {
  boards: PokerStudyBoardListItem[];
  isLoading: boolean;
  errorMessage: string | null;
  onOpenBoard: (boardId: string) => void;
};

type PokerStudyRangesSectionProps = {
  templates: PokerStudyPreflopRangeTemplate[];
  isLoading: boolean;
  errorMessage: string | null;
  onCreateTemplate: () => void;
  onEditTemplate: (template: PokerStudyPreflopRangeTemplate) => void;
  onEditTemplateRange: (template: PokerStudyPreflopRangeTemplate) => void;
  onCopyTemplate: (
    template: PokerStudyPreflopRangeTemplate,
    payload: CopyPokerStudyPreflopRangeTemplatePayload,
  ) => Promise<void>;
  onDeleteTemplate: (template: PokerStudyPreflopRangeTemplate) => void;
};

function PokerStudyRangesSection({
  templates,
  isLoading,
  errorMessage,
  onCreateTemplate,
  onEditTemplate,
  onEditTemplateRange,
  onCopyTemplate,
  onDeleteTemplate,
}: PokerStudyRangesSectionProps) {
  type RangeBlock = 'srp' | 'three_bet';
  type RangeCatalogMode = 'default' | 'personal';
  type RangeRole = 'open_raise' | 'call_vs_open' | 'three_bet' | 'call_vs_3bet';

  type RangeVariantCopyFormState = {
    name: string;
    opponent_profile: string;
    description: string;
    tags_text: string;
  };

  type RangeSequence = {
    role: RangeRole;
    family: string;
    position: string;
    versusPosition: string | null;
    label: string;
    heroLabel: string;
    villainLabel: string | null;
  };

  type RangeRoleTone = {
    accent: string;
    softBg: string;
    panelBg: string;
    border: string;
    chipBg: string;
    chipBorder: string;
  };

  type RangeRoleConfig = {
    block: RangeBlock;
    title: string;
    shortTitle: string;
    description: string;
    heroCopy: string;
    tone: RangeRoleTone;
  };

  const blueTone: RangeRoleTone = {
    accent: '#67e8f9',
    softBg: 'rgba(56, 189, 248, 0.08)',
    panelBg: 'rgba(8, 47, 73, 0.18)',
    border: 'rgba(56, 189, 248, 0.28)',
    chipBg: 'rgba(56, 189, 248, 0.12)',
    chipBorder: 'rgba(56, 189, 248, 0.28)',
  };

  const greenTone: RangeRoleTone = {
    accent: '#86efac',
    softBg: 'rgba(46, 204, 113, 0.08)',
    panelBg: 'rgba(20, 83, 45, 0.16)',
    border: 'rgba(46, 204, 113, 0.28)',
    chipBg: 'rgba(46, 204, 113, 0.12)',
    chipBorder: 'rgba(46, 204, 113, 0.28)',
  };

  const redTone: RangeRoleTone = {
    accent: '#fca5a5',
    softBg: 'rgba(239, 83, 80, 0.08)',
    panelBg: 'rgba(127, 29, 29, 0.16)',
    border: 'rgba(239, 83, 80, 0.3)',
    chipBg: 'rgba(239, 83, 80, 0.13)',
    chipBorder: 'rgba(239, 83, 80, 0.3)',
  };

  const rangeRoleConfig: Record<RangeRole, RangeRoleConfig> = {
    open_raise: {
      block: 'srp',
      title: 'Open Raise',
      shortTitle: 'Hero abre',
      description: 'Rangos con los que hero abre la mano.',
      heroCopy: 'Posición que abre',
      tone: blueTone,
    },
    call_vs_open: {
      block: 'srp',
      title: 'Defensa vs Open',
      shortTitle: 'Hero defiende',
      description: 'Rangos con los que hero responde al open de un rival.',
      heroCopy: 'Posición que defiende',
      tone: greenTone,
    },
    three_bet: {
      block: 'three_bet',
      title: '3Bet',
      shortTitle: 'Hero 3betea',
      description: 'Rangos con los que hero 3betea frente a un open.',
      heroCopy: 'Posición que 3betea',
      tone: redTone,
    },
    call_vs_3bet: {
      block: 'three_bet',
      title: 'Defensa vs 3Bet',
      shortTitle: 'Hero defiende',
      description: 'Rangos con los que hero abrió y continúa frente a un 3Bet.',
      heroCopy: 'Posición que abrió',
      tone: greenTone,
    },
  };

  const rangeSequences: RangeSequence[] = [
    {
      role: 'open_raise',
      family: 'open_raise',
      position: 'hj',
      versusPosition: null,
      label: 'OR · HJ',
      heroLabel: 'HJ',
      villainLabel: null,
    },
    {
      role: 'open_raise',
      family: 'open_raise',
      position: 'co',
      versusPosition: null,
      label: 'OR · CO',
      heroLabel: 'CO',
      villainLabel: null,
    },
    {
      role: 'open_raise',
      family: 'open_raise',
      position: 'btn',
      versusPosition: null,
      label: 'OR · BTN',
      heroLabel: 'BTN',
      villainLabel: null,
    },
    {
      role: 'open_raise',
      family: 'open_raise',
      position: 'sb',
      versusPosition: null,
      label: 'OR · SB',
      heroLabel: 'SB',
      villainLabel: null,
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'bb',
      versusPosition: 'hj',
      label: 'BB vs HJ',
      heroLabel: 'BB',
      villainLabel: 'HJ',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'bb',
      versusPosition: 'co',
      label: 'BB vs CO',
      heroLabel: 'BB',
      villainLabel: 'CO',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'bb',
      versusPosition: 'btn',
      label: 'BB vs BTN',
      heroLabel: 'BB',
      villainLabel: 'BTN',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'bb',
      versusPosition: 'sb',
      label: 'BB vs SB',
      heroLabel: 'BB',
      villainLabel: 'SB',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'sb',
      versusPosition: 'hj',
      label: 'SB vs HJ',
      heroLabel: 'SB',
      villainLabel: 'HJ',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'sb',
      versusPosition: 'co',
      label: 'SB vs CO',
      heroLabel: 'SB',
      villainLabel: 'CO',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'sb',
      versusPosition: 'btn',
      label: 'SB vs BTN',
      heroLabel: 'SB',
      villainLabel: 'BTN',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'btn',
      versusPosition: 'hj',
      label: 'BTN vs HJ',
      heroLabel: 'BTN',
      villainLabel: 'HJ',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'btn',
      versusPosition: 'co',
      label: 'BTN vs CO',
      heroLabel: 'BTN',
      villainLabel: 'CO',
    },
    {
      role: 'call_vs_open',
      family: 'call_vs_open',
      position: 'co',
      versusPosition: 'hj',
      label: 'CO vs HJ',
      heroLabel: 'CO',
      villainLabel: 'HJ',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'bb',
      versusPosition: 'hj',
      label: 'BB vs HJ',
      heroLabel: 'BB',
      villainLabel: 'HJ',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'bb',
      versusPosition: 'co',
      label: 'BB vs CO',
      heroLabel: 'BB',
      villainLabel: 'CO',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'bb',
      versusPosition: 'btn',
      label: 'BB vs BTN',
      heroLabel: 'BB',
      villainLabel: 'BTN',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'bb',
      versusPosition: 'sb',
      label: 'BB vs SB',
      heroLabel: 'BB',
      villainLabel: 'SB',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'sb',
      versusPosition: 'hj',
      label: 'SB vs HJ',
      heroLabel: 'SB',
      villainLabel: 'HJ',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'sb',
      versusPosition: 'co',
      label: 'SB vs CO',
      heroLabel: 'SB',
      villainLabel: 'CO',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'sb',
      versusPosition: 'btn',
      label: 'SB vs BTN',
      heroLabel: 'SB',
      villainLabel: 'BTN',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'btn',
      versusPosition: 'hj',
      label: 'BTN vs HJ',
      heroLabel: 'BTN',
      villainLabel: 'HJ',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'btn',
      versusPosition: 'co',
      label: 'BTN vs CO',
      heroLabel: 'BTN',
      villainLabel: 'CO',
    },
    {
      role: 'three_bet',
      family: 'three_bet',
      position: 'co',
      versusPosition: 'hj',
      label: 'CO vs HJ',
      heroLabel: 'CO',
      villainLabel: 'HJ',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'hj',
      versusPosition: 'co',
      label: 'HJ vs CO',
      heroLabel: 'HJ',
      villainLabel: 'CO',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'hj',
      versusPosition: 'btn',
      label: 'HJ vs BTN',
      heroLabel: 'HJ',
      villainLabel: 'BTN',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'hj',
      versusPosition: 'sb',
      label: 'HJ vs SB',
      heroLabel: 'HJ',
      villainLabel: 'SB',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'hj',
      versusPosition: 'bb',
      label: 'HJ vs BB',
      heroLabel: 'HJ',
      villainLabel: 'BB',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'co',
      versusPosition: 'btn',
      label: 'CO vs BTN',
      heroLabel: 'CO',
      villainLabel: 'BTN',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'co',
      versusPosition: 'sb',
      label: 'CO vs SB',
      heroLabel: 'CO',
      villainLabel: 'SB',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'co',
      versusPosition: 'bb',
      label: 'CO vs BB',
      heroLabel: 'CO',
      villainLabel: 'BB',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'btn',
      versusPosition: 'sb',
      label: 'BTN vs SB',
      heroLabel: 'BTN',
      villainLabel: 'SB',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'btn',
      versusPosition: 'bb',
      label: 'BTN vs BB',
      heroLabel: 'BTN',
      villainLabel: 'BB',
    },
    {
      role: 'call_vs_3bet',
      family: 'call_vs_3bet',
      position: 'sb',
      versusPosition: 'bb',
      label: 'SB vs BB',
      heroLabel: 'SB',
      villainLabel: 'BB',
    },
  ];

  const [rangeCatalogMode, setRangeCatalogMode] = useState<RangeCatalogMode>('default');
  const [selectedPersonalSequence, setSelectedPersonalSequence] =
    useState<RangeSequence | null>(null);
  const [copyingTemplate, setCopyingTemplate] =
    useState<PokerStudyPreflopRangeTemplate | null>(null);
  const [rangeVariantCopyForm, setRangeVariantCopyForm] = useState<RangeVariantCopyFormState>({
    name: '',
    opponent_profile: 'none',
    description: '',
    tags_text: '',
  });
  const [isCopyingTemplate, setIsCopyingTemplate] = useState(false);
  const [srpOpenHeroPosition, setSrpOpenHeroPosition] = useState('hj');
  const [srpDefenseHeroPosition, setSrpDefenseHeroPosition] = useState('bb');
  const [threeBetAggressorHeroPosition, setThreeBetAggressorHeroPosition] = useState('bb');
  const [threeBetDefenseHeroPosition, setThreeBetDefenseHeroPosition] = useState('hj');

  const defaultTemplates = templates.filter((template) => template.is_default);
  const personalTemplates = templates.filter((template) => !template.is_default);
  const visibleTemplates =
    rangeCatalogMode === 'default' ? defaultTemplates : personalTemplates;

  const srpTemplates = visibleTemplates.filter(
    (template) => getRangeTemplateSection(template) === 'srp',
  );
  const threeBetTemplates = visibleTemplates.filter(
    (template) => getRangeTemplateSection(template) === 'three_bet',
  );

  function matchesTemplateSequence(
    template: PokerStudyPreflopRangeTemplate,
    sequence: RangeSequence,
  ): boolean {
    const templateFamily = normalizeRangeFamily(template.range_family);
    const templatePosition = template.position.trim().toLowerCase();
    const templateVersus = template.versus_position?.trim().toLowerCase() || null;

    return (
      templateFamily === normalizeRangeFamily(sequence.family) &&
      templatePosition === sequence.position.trim().toLowerCase() &&
      templateVersus === (sequence.versusPosition?.trim().toLowerCase() || null)
    );
  }

  function getTemplatesForSequence(
    sequence: RangeSequence,
    sourceTemplates: PokerStudyPreflopRangeTemplate[] = visibleTemplates,
  ): PokerStudyPreflopRangeTemplate[] {
    return sourceTemplates.filter((template) => matchesTemplateSequence(template, sequence));
  }

  function findDefaultTemplate(sequence: RangeSequence): PokerStudyPreflopRangeTemplate | null {
    return getTemplatesForSequence(sequence, defaultTemplates)[0] || null;
  }

  function openCopyTemplateDialog(template: PokerStudyPreflopRangeTemplate) {
    setCopyingTemplate(template);
    setRangeVariantCopyForm({
      name: `${template.name} · Personal`,
      opponent_profile: 'none',
      description: template.description || '',
      tags_text: template.tags.join(', '),
    });
  }

  function closeCopyTemplateDialog() {
    if (isCopyingTemplate) return;

    setCopyingTemplate(null);
    setRangeVariantCopyForm({
      name: '',
      opponent_profile: 'none',
      description: '',
      tags_text: '',
    });
  }

  async function handleCopyTemplate() {
    if (!copyingTemplate || !rangeVariantCopyForm.name.trim()) return;

    setIsCopyingTemplate(true);

    try {
      await onCopyTemplate(copyingTemplate, {
        name: rangeVariantCopyForm.name.trim(),
        opponent_profile:
          rangeVariantCopyForm.opponent_profile === 'none'
            ? null
            : rangeVariantCopyForm.opponent_profile,
        description: rangeVariantCopyForm.description,
        tags: rangeVariantCopyForm.tags_text
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      });
      setRangeCatalogMode('personal');
      setSelectedPersonalSequence(null);
      setCopyingTemplate(null);
      setRangeVariantCopyForm({
        name: '',
        opponent_profile: 'none',
        description: '',
        tags_text: '',
      });
    } catch {
      return;
    } finally {
      setIsCopyingTemplate(false);
    }
  }

  function getSequencesForRole(role: RangeRole): RangeSequence[] {
    return rangeSequences.filter((sequence) => sequence.role === role);
  }

  function getHeroPositionsForRole(role: RangeRole): string[] {
    return Array.from(new Set(getSequencesForRole(role).map((sequence) => sequence.position)));
  }

  function countExistingSequences(sequences: RangeSequence[]): number {
    return sequences.filter((sequence) => getTemplatesForSequence(sequence).length > 0).length;
  }

  function renderEmptyRangeRow(sequence: RangeSequence, roleConfig: RangeRoleConfig) {
    const tone = roleConfig.tone;
    const defaultTemplate = findDefaultTemplate(sequence);

    return (
      <Box
        key={`${sequence.family}-${sequence.position}-${sequence.versusPosition || 'none'}`}
        sx={{
          borderRadius: 2,
          border: '1px dashed rgba(148, 163, 184, 0.22)',
          bgcolor: 'rgba(15, 23, 42, 0.16)',
          px: 1.25,
          py: 0.95,
          minHeight: 52,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            title={sequence.label}
            sx={{
              color: '#cbd5e1',
              fontSize: 15,
              fontWeight: 950,
              lineHeight: 1.1,
            }}
          >
            {sequence.label}
          </Typography>

          <Typography
            sx={{
              color: '#64748b',
              fontSize: 12,
              fontWeight: 800,
              mt: 0.25,
            }}
          >
            {rangeCatalogMode === 'personal' ? 'Sin variantes personales' : 'No disponible'}
          </Typography>
        </Box>

        {rangeCatalogMode === 'personal' ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (defaultTemplate) {
                openCopyTemplateDialog(defaultTemplate);
                return;
              }

              onCreateTemplate();
            }}
            sx={{
              justifySelf: { xs: 'flex-start', md: 'flex-end' },
              minHeight: 28,
              px: 1.15,
              borderRadius: 1.5,
              fontSize: 11.5,
              fontWeight: 950,
              textTransform: 'none',
              color: tone.accent,
              borderColor: alpha(tone.accent, 0.32),
              '&:hover': {
                borderColor: alpha(tone.accent, 0.52),
                bgcolor: alpha(tone.accent, 0.08),
              },
            }}
          >
            {defaultTemplate ? 'Crear variante' : 'Crear manual'}
          </Button>
        ) : null}
      </Box>
    );
  }

  function renderPersonalRangeGroup(sequence: RangeSequence, roleConfig: RangeRoleConfig) {
    const tone = roleConfig.tone;
    const personalVariants = getTemplatesForSequence(sequence, personalTemplates);
    const defaultTemplate = findDefaultTemplate(sequence);

    if (personalVariants.length === 0) {
      return renderEmptyRangeRow(sequence, roleConfig);
    }

    return (
      <Box
        key={`${sequence.family}-${sequence.position}-${sequence.versusPosition || 'none'}`}
        role="button"
        tabIndex={0}
        onClick={() => setSelectedPersonalSequence(sequence)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setSelectedPersonalSequence(sequence);
          }
        }}
        sx={{
          cursor: 'pointer',
          borderRadius: 2,
          border: `1px solid ${alpha(tone.accent, 0.24)}`,
          bgcolor: 'rgba(2, 6, 23, 0.34)',
          px: 1.25,
          py: 0.95,
          minHeight: 52,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto auto' },
          gap: 1,
          alignItems: 'center',
          transition: 'border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease',
          '&:hover': {
            borderColor: alpha(tone.accent, 0.54),
            bgcolor: alpha(tone.accent, 0.08),
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            title={sequence.label}
            sx={{ color: '#f8fafc', fontSize: 15.5, fontWeight: 950, lineHeight: 1.1 }}
          >
            {sequence.label}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 11.5, fontWeight: 800, mt: 0.25 }}>
            {personalVariants.length === 1
              ? '1 variante personal'
              : `${personalVariants.length} variantes personales`}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={`${personalVariants.length} variantes`}
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 950,
            color: tone.accent,
            bgcolor: alpha(tone.accent, 0.1),
            border: `1px solid ${alpha(tone.accent, 0.22)}`,
          }}
        />

        <Stack
          direction="row"
          spacing={0.45}
          sx={{ justifySelf: { xs: 'flex-start', md: 'flex-end' } }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedPersonalSequence(sequence);
            }}
            sx={{ minHeight: 26, fontSize: 11, fontWeight: 900, textTransform: 'none' }}
          >
            Ver variantes
          </Button>

          {defaultTemplate ? (
            <Button
              size="small"
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                openCopyTemplateDialog(defaultTemplate);
              }}
              sx={{ minHeight: 26, fontSize: 11, fontWeight: 900, textTransform: 'none' }}
            >
              + Añadir
            </Button>
          ) : null}
        </Stack>
      </Box>
    );
  }

  function renderDefaultRangeRow(sequence: RangeSequence, roleConfig: RangeRoleConfig) {
    const template = getTemplatesForSequence(sequence, defaultTemplates)[0] || null;
    const tone = roleConfig.tone;

    if (!template) {
      return renderEmptyRangeRow(sequence, roleConfig);
    }

    const comboCount = getTemplateComboCount(template);
    const percentage = formatTemplatePercentage(comboCount);

    return (
      <Box
        key={`${sequence.family}-${sequence.position}-${sequence.versusPosition || 'none'}`}
        role="button"
        tabIndex={0}
        onClick={() => onEditTemplateRange(template)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onEditTemplateRange(template);
          }
        }}
        sx={{
          cursor: 'pointer',
          borderRadius: 2,
          border: `1px solid ${alpha(tone.accent, 0.24)}`,
          bgcolor: 'rgba(2, 6, 23, 0.34)',
          px: 1.25,
          py: 0.95,
          minHeight: 52,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto auto' },
          gap: 1,
          alignItems: 'center',
          transition: 'border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease',
          '&:hover': {
            borderColor: alpha(tone.accent, 0.54),
            bgcolor: alpha(tone.accent, 0.08),
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            title={sequence.label}
            sx={{ color: '#f8fafc', fontSize: 15.5, fontWeight: 950, lineHeight: 1.1 }}
          >
            {sequence.label}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 11.5, fontWeight: 800, mt: 0.25 }}>
            Consulta el estándar PROA
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.55} alignItems="center">
          <Chip
            size="small"
            label="PROA"
            sx={{
              height: 22,
              fontSize: 10.5,
              fontWeight: 950,
              color: '#67e8f9',
              bgcolor: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.24)',
            }}
          />
          <Chip
            size="small"
            label={`${comboCount} combos`}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 900,
              color: '#e2e8f0',
              bgcolor: 'rgba(148, 163, 184, 0.1)',
            }}
          />
          <Chip
            size="small"
            label={percentage}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 950,
              color: tone.accent,
              bgcolor: alpha(tone.accent, 0.1),
              border: `1px solid ${alpha(tone.accent, 0.22)}`,
            }}
          />
        </Stack>

        <Button
          size="small"
          variant="outlined"
          onClick={(event) => {
            event.stopPropagation();
            openCopyTemplateDialog(template);
          }}
          sx={{
            minHeight: 26,
            px: 0.9,
            borderRadius: 1.35,
            fontSize: 11,
            fontWeight: 900,
            textTransform: 'none',
            color: '#67e8f9',
            borderColor: 'rgba(56, 189, 248, 0.3)',
          }}
        >
          Crear variante
        </Button>
      </Box>
    );
  }

  function renderRangeRow(sequence: RangeSequence, roleConfig: RangeRoleConfig) {
    return rangeCatalogMode === 'personal'
      ? renderPersonalRangeGroup(sequence, roleConfig)
      : renderDefaultRangeRow(sequence, roleConfig);
  }


  function renderRolePanel({
    role,
    activeHeroPosition,
    onHeroPositionChange,
  }: {
    role: RangeRole;
    activeHeroPosition: string;
    onHeroPositionChange: (position: string) => void;
  }) {
    const roleConfig = rangeRoleConfig[role];
    const roleSequences = getSequencesForRole(role);
    const heroPositions = getHeroPositionsForRole(role);
    const selectedHeroPosition = heroPositions.includes(activeHeroPosition)
      ? activeHeroPosition
      : heroPositions[0] || '';
    const visibleSequences = roleSequences.filter(
      (sequence) => sequence.position === selectedHeroPosition,
    );
    const roleExistingCount = countExistingSequences(roleSequences);
    const visibleExistingCount = countExistingSequences(visibleSequences);
    const tone = roleConfig.tone;

    return (
      <Box
        sx={{
          borderRadius: 2.75,
          border: `1px solid ${tone.border}`,
          bgcolor: tone.panelBg,
          overflow: 'hidden',
          minHeight: 390,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 1.3,
            py: 1.15,
            borderBottom: `1px solid ${tone.border}`,
            bgcolor: tone.softBg,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography
                sx={{
                  color: tone.accent,
                  fontSize: 20,
                  fontWeight: 950,
                  lineHeight: 1.05,
                  letterSpacing: '-0.25px',
                }}
              >
                {roleConfig.title} · Hero {selectedHeroPosition.toUpperCase()}
              </Typography>

              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontSize: 12.5,
                  fontWeight: 750,
                  mt: 0.45,
                }}
              >
                {roleConfig.description}
              </Typography>
            </Box>

            <Chip
              label={`${visibleExistingCount}/${visibleSequences.length} rangos`}
              sx={{
                height: 26,
                fontSize: 11.5,
                fontWeight: 950,
                color: '#f8fafc',
                bgcolor: tone.chipBg,
                border: `1px solid ${tone.chipBorder}`,
              }}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '150px minmax(0, 1fr)' },
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRight: {
                xs: 'none',
                md: `1px solid ${alpha(tone.accent, 0.12)}`,
              },
              borderBottom: {
                xs: `1px solid ${alpha(tone.accent, 0.12)}`,
                md: 'none',
              },
              bgcolor: 'rgba(2, 6, 23, 0.16)',
            }}
          >
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: 10.5,
                fontWeight: 950,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                mb: 0.8,
              }}
            >
              {roleConfig.heroCopy}
            </Typography>

            <Stack
              direction={{ xs: 'row', md: 'column' }}
              spacing={0.55}
              flexWrap={{ xs: 'wrap', md: 'nowrap' }}
            >
              {heroPositions.map((position) => {
                const isActive = selectedHeroPosition === position;
                const positionSequences = roleSequences.filter(
                  (sequence) => sequence.position === position,
                );
                const existingCount = countExistingSequences(positionSequences);

                return (
                  <Button
                    key={position}
                    size="small"
                    variant={isActive ? 'contained' : 'outlined'}
                    onClick={() => onHeroPositionChange(position)}
                    sx={{
                      minHeight: 32,
                      justifyContent: 'space-between',
                      borderRadius: 1.65,
                      px: 0.9,
                      fontWeight: 950,
                      textTransform: 'uppercase',
                      color: isActive ? '#020617' : tone.accent,
                      bgcolor: isActive ? tone.accent : alpha(tone.accent, 0.04),
                      borderColor: alpha(tone.accent, isActive ? 0.5 : 0.24),
                      '&:hover': {
                        bgcolor: isActive ? tone.accent : alpha(tone.accent, 0.1),
                        borderColor: alpha(tone.accent, 0.42),
                      },
                    }}
                  >
                    <Box component="span">{position.toUpperCase()}</Box>
                    <Box component="span">
                      {existingCount}/{positionSequences.length}
                    </Box>
                  </Button>
                );
              })}
            </Stack>
          </Box>

          <Box
            sx={{
              p: 1.15,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {visibleSequences.length > 0 ? (
              <Stack spacing={0.75}>
                {visibleSequences.map((sequence) => renderRangeRow(sequence, roleConfig))}
              </Stack>
            ) : (
              <Box
                sx={{
                  minHeight: 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: '#64748b',
                  fontWeight: 850,
                }}
              >
                No hay secuencias válidas para esta selección.
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            px: 1.25,
            py: 0.75,
            borderTop: `1px solid ${alpha(tone.accent, 0.1)}`,
            color: '#64748b',
            fontSize: 11,
            fontWeight: 850,
          }}
        >
          Cobertura total: {roleExistingCount}/{roleSequences.length}
        </Box>
      </Box>
    );
  }

  function renderRangeBlock({
    block,
    title,
    subtitle,
    totalCount,
    leftRole,
    leftHeroPosition,
    onLeftHeroPositionChange,
    rightRole,
    rightHeroPosition,
    onRightHeroPositionChange,
  }: {
    block: RangeBlock;
    title: string;
    subtitle: string;
    totalCount: number;
    leftRole: RangeRole;
    leftHeroPosition: string;
    onLeftHeroPositionChange: (position: string) => void;
    rightRole: RangeRole;
    rightHeroPosition: string;
    onRightHeroPositionChange: (position: string) => void;
  }) {
    const leftRoleConfig = rangeRoleConfig[leftRole];
    const rightRoleConfig = rangeRoleConfig[rightRole];

    return (
      <Card
        key={block}
        variant="outlined"
        sx={{
          borderRadius: 3,
          bgcolor: 'rgba(15, 23, 42, 0.2)',
          borderColor: 'rgba(148, 163, 184, 0.18)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: 11,
                    fontWeight: 950,
                    letterSpacing: 0.7,
                    textTransform: 'uppercase',
                    lineHeight: 1.1,
                  }}
                >
                  Tipo de bote
                </Typography>

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontSize: 22,
                    fontWeight: 950,
                    lineHeight: 1.05,
                    mt: 0.35,
                  }}
                >
                  {title}
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 13,
                    fontWeight: 750,
                    mt: 0.4,
                  }}
                >
                  {subtitle}
                </Typography>
              </Box>

              <Chip
                label={`${totalCount} rangos`}
                sx={{
                  height: 28,
                  fontSize: 12,
                  fontWeight: 950,
                  color: '#f8fafc',
                  bgcolor: alpha('#38bdf8', 0.1),
                  border: `1px solid ${alpha('#38bdf8', 0.24)}`,
                }}
              />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
                gap: 1.25,
                alignItems: 'stretch',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: leftRoleConfig.tone.accent,
                    fontSize: 11,
                    fontWeight: 950,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    mb: 0.65,
                  }}
                >
                  {leftRoleConfig.shortTitle}
                </Typography>

                {renderRolePanel({
                  role: leftRole,
                  activeHeroPosition: leftHeroPosition,
                  onHeroPositionChange: onLeftHeroPositionChange,
                })}
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: rightRoleConfig.tone.accent,
                    fontSize: 11,
                    fontWeight: 950,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    mb: 0.65,
                  }}
                >
                  {rightRoleConfig.shortTitle}
                </Typography>

                {renderRolePanel({
                  role: rightRole,
                  activeHeroPosition: rightHeroPosition,
                  onHeroPositionChange: onRightHeroPositionChange,
                })}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const selectedPersonalVariants = selectedPersonalSequence
    ? getTemplatesForSequence(selectedPersonalSequence, personalTemplates)
    : [];
  const selectedDefaultTemplate = selectedPersonalSequence
    ? findDefaultTemplate(selectedPersonalSequence)
    : null;


  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: 260,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="error" sx={{ mb: 2.5 }}>
        {errorMessage}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          bgcolor: 'rgba(15, 23, 42, 0.18)',
          borderColor: 'rgba(148, 163, 184, 0.2)',
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                RANGOS PREFLOP
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rangeCatalogMode === 'default'
                  ? 'Consulta los estándares PROA y crea una copia personal cuando quieras adaptarlos.'
                  : 'Gestiona tus copias personales y tus plantillas propias sin alterar los estándares PROA.'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Button
                size="small"
                variant={rangeCatalogMode === 'default' ? 'contained' : 'outlined'}
                onClick={() => setRangeCatalogMode('default')}
                sx={{ minHeight: 34, borderRadius: 2, fontWeight: 950, textTransform: 'none' }}
              >
                Predeterminados PROA · {defaultTemplates.length}
              </Button>

              <Button
                size="small"
                variant={rangeCatalogMode === 'personal' ? 'contained' : 'outlined'}
                onClick={() => setRangeCatalogMode('personal')}
                sx={{ minHeight: 34, borderRadius: 2, fontWeight: 950, textTransform: 'none' }}
              >
                Mis rangos · {personalTemplates.length}
              </Button>

              {rangeCatalogMode === 'personal' ? (
                <Button
                  variant="contained"
                  onClick={onCreateTemplate}
                  sx={{
                    minHeight: 36,
                    borderRadius: 2,
                    px: 2,
                    fontWeight: 950,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 14px 30px rgba(56,189,248,0.18)',
                  }}
                >
                  Nueva plantilla
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {templates.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.2)',
            borderColor: 'rgba(148, 163, 184, 0.22)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={850}>
              Todavía no hay plantillas de rango
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crea tu primer rango base para empezar a construir el mapa preflop.
            </Typography>
            <Button variant="contained" onClick={onCreateTemplate}>
              Crear primera plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {renderRangeBlock({
            block: 'srp',
            title: 'SRP',
            subtitle: 'Open Raise y defensa frente a open.',
            totalCount: srpTemplates.length,
            leftRole: 'open_raise',
            leftHeroPosition: srpOpenHeroPosition,
            onLeftHeroPositionChange: setSrpOpenHeroPosition,
            rightRole: 'call_vs_open',
            rightHeroPosition: srpDefenseHeroPosition,
            onRightHeroPositionChange: setSrpDefenseHeroPosition,
          })}

          {renderRangeBlock({
            block: 'three_bet',
            title: '3Bet',
            subtitle: '3Bet y defensa frente a 3Bet.',
            totalCount: threeBetTemplates.length,
            leftRole: 'three_bet',
            leftHeroPosition: threeBetAggressorHeroPosition,
            onLeftHeroPositionChange: setThreeBetAggressorHeroPosition,
            rightRole: 'call_vs_3bet',
            rightHeroPosition: threeBetDefenseHeroPosition,
            onRightHeroPositionChange: setThreeBetDefenseHeroPosition,
          })}
        </Stack>
      )}

      <Dialog
        open={Boolean(selectedPersonalSequence)}
        onClose={() => setSelectedPersonalSequence(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            VARIANTES PERSONALES
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {selectedPersonalSequence?.label || 'Secuencia'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={0.9} sx={{ pt: 0.75 }}>
            {selectedPersonalVariants.map((template) => {
              const comboCount = getTemplateComboCount(template);

              return (
                <Box
                  key={template.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto auto' },
                    gap: 1,
                    alignItems: 'center',
                    borderRadius: 2,
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                    bgcolor: 'rgba(15, 23, 42, 0.38)',
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap title={template.name} fontWeight={950}>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Perfil: {formatRangeTemplateOpponentProfileLabel(template.opponent_profile)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.55}>
                    <Chip size="small" label={`${comboCount} combos`} />
                    <Chip size="small" label={formatTemplatePercentage(comboCount)} />
                  </Stack>

                  <Stack direction="row" spacing={0.35} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedPersonalSequence(null);
                        onEditTemplateRange(template);
                      }}
                    >
                      Abrir
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedPersonalSequence(null);
                        onEditTemplate(template);
                      }}
                    >
                      Meta
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedPersonalSequence(null);
                        onDeleteTemplate(template);
                      }}
                    >
                      ×
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions>
          {selectedDefaultTemplate ? (
            <Button
              onClick={() => {
                setSelectedPersonalSequence(null);
                openCopyTemplateDialog(selectedDefaultTemplate);
              }}
            >
              Crear variante desde PROA
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setSelectedPersonalSequence(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(copyingTemplate)}
        onClose={closeCopyTemplateDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(56, 189, 248, 0.28)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            NUEVA VARIANTE PERSONAL
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {copyingTemplate?.name || 'Rango PROA'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            La matriz se copiará exactamente desde el estándar PROA. Después podrás modificarla libremente.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 0.75 }}>
            <TextField
              label="Nombre de la variante"
              value={rangeVariantCopyForm.name}
              onChange={(event) =>
                setRangeVariantCopyForm((current) => ({ ...current, name: event.target.value }))
              }
              fullWidth
              helperText="Ejemplo: Regular estándar, Recreacional loose, Pool NL50"
            />

            <TextField
              select
              label="Perfil del rival"
              value={rangeVariantCopyForm.opponent_profile}
              onChange={(event) =>
                setRangeVariantCopyForm((current) => ({
                  ...current,
                  opponent_profile: event.target.value,
                }))
              }
              fullWidth
            >
              {RANGE_TEMPLATE_OPPONENT_PROFILE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Descripción"
              value={rangeVariantCopyForm.description}
              onChange={(event) =>
                setRangeVariantCopyForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />

            <TextField
              label="Tags"
              value={rangeVariantCopyForm.tags_text}
              onChange={(event) =>
                setRangeVariantCopyForm((current) => ({
                  ...current,
                  tags_text: event.target.value,
                }))
              }
              fullWidth
              helperText="Separados por coma. Ejemplo: 50nl, pool, exploit"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeCopyTemplateDialog} disabled={isCopyingTemplate}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCopyTemplate()}
            disabled={isCopyingTemplate || !rangeVariantCopyForm.name.trim()}
            sx={{ fontWeight: 900 }}
          >
            {isCopyingTemplate ? 'Creando...' : 'Crear variante'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function PokerStudyBoardsSection({
  boards,
  isLoading,
  errorMessage,
  onOpenBoard,
}: PokerStudyBoardsSectionProps) {
  const [boardSearchText, setBoardSearchText] = useState('');
  const [boardSpotTypeFilter, setBoardSpotTypeFilter] = useState('all');
  const [boardPositionFilter, setBoardPositionFilter] = useState('all');
  const [boardVillainProfileFilter, setBoardVillainProfileFilter] = useState('all');
  const [boardHighCardFilter, setBoardHighCardFilter] = useState('all');
  const [boardPairFilter, setBoardPairFilter] = useState('all');
  const [boardBroadwayFilter, setBoardBroadwayFilter] = useState('all');
  const [boardSuitFilter, setBoardSuitFilter] = useState('all');
  const [boardConnectivityFilter, setBoardConnectivityFilter] = useState('all');
  const [boardStraightFilter, setBoardStraightFilter] = useState('all');
  const [boardLowCardsFilter, setBoardLowCardsFilter] = useState('all');

  const filteredBoards = useMemo(
    () =>
      boards.filter((board) => {
        const normalizedSearch = boardSearchText.trim().toLowerCase();
        const broadwayCount = getBoardBroadwayCount(board);

        const boardLabel = buildBoardDisplayLabel(board).toLowerCase();
        const searchableText = [
          boardLabel,
          board.spot_title,
          board.spot_type,
          board.hero_position,
          board.villain_position,
          board.villain_profile || '',
          board.high_card_rank || '',
          board.high_card_value !== null && board.high_card_value !== undefined
            ? String(board.high_card_value)
            : '',
          board.paired_status || '',
          board.paired_rank || '',
          board.suited_texture || '',
          board.flush_draw_possible ? 'fd flush draw proyecto color' : '',
          board.made_flush_possible ? 'monotone color hecho' : '',
          board.connectivity_texture || '',
          board.straight_possible ? 'straight escalera posible' : '',
          board.broadway_density || '',
          broadwayCount !== null ? `${broadwayCount} broadways` : '',
          board.low_card_count !== null && board.low_card_count !== undefined
            ? `${board.low_card_count} low cards`
            : '',
          board.low_board ? 'low' : '',
          ...board.board_tags,
        ]
          .join(' ')
          .toLowerCase();

        const matchesSearch =
          !normalizedSearch || searchableText.includes(normalizedSearch);

        const matchesSpotType =
          boardSpotTypeFilter === 'all' ||
          board.spot_type.trim().toLowerCase() === boardSpotTypeFilter;

        const matchesPosition =
          boardPositionFilter === 'all' ||
          board.hero_position.trim().toLowerCase() === boardPositionFilter ||
          board.villain_position.trim().toLowerCase() === boardPositionFilter;

        const normalizedVillainProfile = (board.villain_profile || '').trim().toLowerCase();

        const matchesVillainProfile =
          boardVillainProfileFilter === 'all' ||
          (boardVillainProfileFilter === 'none' && !normalizedVillainProfile) ||
          normalizedVillainProfile === boardVillainProfileFilter;

        const matchesHighCard = matchesBoardHighCardFilter(board, boardHighCardFilter);

        const matchesPair =
          boardPairFilter === 'all' ||
          board.paired_status?.trim().toLowerCase() === boardPairFilter;

        const matchesBroadways =
          boardBroadwayFilter === 'all' ||
          broadwayCount === Number(boardBroadwayFilter);

        const matchesSuit =
          boardSuitFilter === 'all' ||
          board.suited_texture?.trim().toLowerCase() === boardSuitFilter;

        const matchesConnectivity =
          boardConnectivityFilter === 'all' ||
          board.connectivity_texture?.trim().toLowerCase() === boardConnectivityFilter;

        const matchesStraight = matchesBoardBooleanFilter(
          board.straight_possible,
          boardStraightFilter,
        );

        const matchesLowCards =
          boardLowCardsFilter === 'all' ||
          board.low_card_count === Number(boardLowCardsFilter);

        return (
          matchesSearch &&
          matchesSpotType &&
          matchesPosition &&
          matchesVillainProfile &&
          matchesHighCard &&
          matchesPair &&
          matchesBroadways &&
          matchesSuit &&
          matchesConnectivity &&
          matchesStraight &&
          matchesLowCards
        );
      }),
    [
      boards,
      boardBroadwayFilter,
      boardConnectivityFilter,
      boardHighCardFilter,
      boardLowCardsFilter,
      boardPairFilter,
      boardPositionFilter,
      boardSearchText,
      boardVillainProfileFilter,
      boardSpotTypeFilter,
      boardStraightFilter,
      boardSuitFilter,
    ],
  );

  const hasActiveBoardFilters =
    Boolean(boardSearchText.trim()) ||
    boardSpotTypeFilter !== 'all' ||
    boardPositionFilter !== 'all' ||
    boardVillainProfileFilter !== 'all' ||
    boardHighCardFilter !== 'all' ||
    boardPairFilter !== 'all' ||
    boardBroadwayFilter !== 'all' ||
    boardSuitFilter !== 'all' ||
    boardConnectivityFilter !== 'all' ||
    boardStraightFilter !== 'all' ||
    boardLowCardsFilter !== 'all';

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: 260,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="error" sx={{ mb: 2.5 }}>
        {errorMessage}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          bgcolor: 'rgba(15, 23, 42, 0.18)',
          borderColor: 'rgba(148, 163, 184, 0.2)',
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={950}>
                  BOARDS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vista agregada de todos los boards creados en tus spots.
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Chip
                  label={`${filteredBoards.length}/${boards.length} visibles`}
                  sx={{
                    fontWeight: 950,
                    bgcolor: alpha('#38bdf8', 0.1),
                    border: `1px solid ${alpha('#38bdf8', 0.24)}`,
                  }}
                />

                {hasActiveBoardFilters ? (
                  <Chip
                    label="Limpiar filtros"
                    variant="outlined"
                    onClick={() => {
                      setBoardSearchText('');
                      setBoardSpotTypeFilter('all');
                      setBoardPositionFilter('all');
                      setBoardVillainProfileFilter('all');
                      setBoardHighCardFilter('all');
                      setBoardPairFilter('all');
                      setBoardBroadwayFilter('all');
                      setBoardSuitFilter('all');
                      setBoardConnectivityFilter('all');
                      setBoardStraightFilter('all');
                      setBoardLowCardsFilter('all');
                    }}
                    sx={{ fontWeight: 900 }}
                  />
                ) : null}
              </Stack>
            </Stack>

            <Stack
              spacing={1.15}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.25,
                  bgcolor: 'rgba(255,255,255,0.035)',
                  transition:
                    'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.055)',
                    boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.16)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.18)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 850,
                },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.15} alignItems="stretch">
                <TextField
                  size="small"
                  label="Buscar board, spot, tag..."
                  value={boardSearchText}
                  onChange={(event) => setBoardSearchText(event.target.value)}
                  sx={{ flex: 1.35, minWidth: { xs: '100%', md: 260 } }}
                />

                <TextField
                  select
                  size="small"
                  label="Tipo"
                  value={boardSpotTypeFilter}
                  onChange={(event) => setBoardSpotTypeFilter(event.target.value)}
                  sx={{ flex: 0.7, minWidth: { xs: '100%', md: 135 } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="srp">SRP</MenuItem>
                  <MenuItem value="3bet">3BET</MenuItem>
                  <MenuItem value="4bet">4BET</MenuItem>
                  <MenuItem value="rol">ROL</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Posición"
                  value={boardPositionFilter}
                  onChange={(event) => setBoardPositionFilter(event.target.value)}
                  sx={{ flex: 0.7, minWidth: { xs: '100%', md: 135 } }}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {POSITION_OPTIONS.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Rival"
                  value={boardVillainProfileFilter}
                  onChange={(event) => setBoardVillainProfileFilter(event.target.value)}
                  sx={{ flex: 0.7, minWidth: { xs: '100%', md: 150 } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="recreational">Recreacional</MenuItem>
                  <MenuItem value="unknown">Desconocido</MenuItem>
                  <MenuItem value="none">Sin perfil</MenuItem>
                </TextField>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.15} alignItems="stretch">
                <TextField
                  select
                  size="small"
                  label="Carta más alta"
                  value={boardHighCardFilter}
                  onChange={(event) => setBoardHighCardFilter(event.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 145 } }}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="14">A-high</MenuItem>
                  <MenuItem value="13">K-high</MenuItem>
                  <MenuItem value="12">Q-high</MenuItem>
                  <MenuItem value="11">J-high</MenuItem>
                  <MenuItem value="10">T-high</MenuItem>
                  <MenuItem value="9_or_lower">9 o menos</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Pareja"
                  value={boardPairFilter}
                  onChange={(event) => setBoardPairFilter(event.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 145 } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="unpaired">Sin pareja</MenuItem>
                  <MenuItem value="paired">Emparejado</MenuItem>
                  <MenuItem value="trips_board">Trips</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Broadways"
                  value={boardBroadwayFilter}
                  onChange={(event) => setBoardBroadwayFilter(event.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 145 } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="0">0 broadways</MenuItem>
                  <MenuItem value="1">1 broadway</MenuItem>
                  <MenuItem value="2">2 broadways</MenuItem>
                  <MenuItem value="3">3 broadways</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Palos"
                  value={boardSuitFilter}
                  onChange={(event) => setBoardSuitFilter(event.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', md: 145 } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="rainbow">Rainbow</MenuItem>
                  <MenuItem value="two_tone">Two-tone</MenuItem>
                  <MenuItem value="monotone">Monotone</MenuItem>
                </TextField>
              </Stack>

              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(148, 163, 184, 0.14)',
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  sx={{
                    minHeight: 38,
                    '& .MuiAccordionSummary-content': {
                      my: 0.8,
                    },
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={950}>
                    Filtros avanzados
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0, pb: 1.35 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.15} alignItems="stretch">
                    <TextField
                      select
                      size="small"
                      label="Conectividad"
                      value={boardConnectivityFilter}
                      onChange={(event) => setBoardConnectivityFilter(event.target.value)}
                      sx={{ flex: 1, minWidth: { xs: '100%', md: 150 } }}
                    >
                      <MenuItem value="all">Todas</MenuItem>
                      <MenuItem value="connected">Connected</MenuItem>
                      <MenuItem value="two_gap">Two-gap</MenuItem>
                      <MenuItem value="one_gap">One-gap</MenuItem>
                      <MenuItem value="disconnected">Disconnected</MenuItem>
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="Escalera posible"
                      value={boardStraightFilter}
                      onChange={(event) => setBoardStraightFilter(event.target.value)}
                      sx={{ flex: 1, minWidth: { xs: '100%', md: 150 } }}
                    >
                      <MenuItem value="all">Todas</MenuItem>
                      <MenuItem value="yes">Sí</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="Cartas bajas"
                      value={boardLowCardsFilter}
                      onChange={(event) => setBoardLowCardsFilter(event.target.value)}
                      sx={{ flex: 1, minWidth: { xs: '100%', md: 150 } }}
                    >
                      <MenuItem value="all">Todas</MenuItem>
                      <MenuItem value="0">0 bajas</MenuItem>
                      <MenuItem value="1">1 baja</MenuItem>
                      <MenuItem value="2">2 bajas</MenuItem>
                      <MenuItem value="3">3 bajas</MenuItem>
                    </TextField>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {boards.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.2)',
            borderColor: 'rgba(148, 163, 184, 0.22)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={850}>
              Todavía no hay boards
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crea boards desde un spot para empezar a construir tu biblioteca agregada.
            </Typography>
          </CardContent>
        </Card>
      ) : filteredBoards.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.2)',
            borderColor: 'rgba(148, 163, 184, 0.22)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={850}>
              Ningún board coincide con los filtros
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ajusta la búsqueda, el tipo, la posición o la textura para volver a mostrar resultados.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setBoardSearchText('');
                setBoardSpotTypeFilter('all');
                setBoardPositionFilter('all');
              }}
            >
              Limpiar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredBoards.map((board) => (
            <Grid item xs={12} sm={6} lg={3} key={board.id}>
              <Card
                variant="outlined"
                onClick={() => onOpenBoard(board.id)}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'rgba(15, 23, 42, 0.2)',
                  borderColor: 'rgba(148, 163, 184, 0.18)',
                  boxShadow: '0 14px 34px rgba(15, 23, 42, 0.12)',
                  transition:
                    'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    bgcolor: 'rgba(15, 23, 42, 0.32)',
                    borderColor: 'rgba(76, 201, 216, 0.46)',
                    boxShadow: '0 22px 48px rgba(15, 23, 42, 0.24)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.55, '&:last-child': { pb: 1.55 } }}>
                  <Stack spacing={1.05}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={getBoardSpotTypeChipStyle(board.spot_type).label}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: 0.25,
                            color: getBoardSpotTypeChipStyle(board.spot_type).color,
                            bgcolor: getBoardSpotTypeChipStyle(board.spot_type).bgcolor,
                            border: `1px solid ${getBoardSpotTypeChipStyle(board.spot_type).borderColor}`,
                          }}
                        />

                        <Chip
                          size="small"
                          label={`${board.hero_position.toUpperCase()} vs ${board.villain_position.toUpperCase()}`}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 900,
                            color: '#bfdbfe',
                            bgcolor: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.26)',
                          }}
                        />
                      </Stack>

                      {board.effective_stack_bb !== null && board.effective_stack_bb !== undefined ? (
                        <Chip
                          size="small"
                          label={`${board.effective_stack_bb}bb`}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 950,
                            flexShrink: 0,
                            color: '#86efac',
                            bgcolor: 'rgba(34, 197, 94, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.28)',
                          }}
                        />
                      ) : null}
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ minWidth: 0 }}
                    >
                      {board.board_mode === 'exact_board' ? (
                        <Stack direction="row" spacing={0.45} alignItems="center">
                          <PokerStudyBoardMiniCard value={board.flop_card_1} />
                          <PokerStudyBoardMiniCard value={board.flop_card_2} />
                          <PokerStudyBoardMiniCard value={board.flop_card_3} />
                        </Stack>
                      ) : (
                        <Typography variant="h6" fontWeight={950} noWrap>
                          {buildBoardDisplayLabel(board)}
                        </Typography>
                      )}

                      <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                        <Typography
                          noWrap
                          sx={{
                            color: '#f8fafc',
                            fontSize: 13,
                            fontWeight: 950,
                            lineHeight: 1.1,
                          }}
                        >
                          {board.spot_title}
                        </Typography>

                        <Typography
                          noWrap
                          sx={{
                            mt: 0.35,
                            color: '#94a3b8',
                            fontSize: 11.5,
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          Rival · {formatBoardVillainProfileLabel(board.villain_profile)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.45} flexWrap="wrap">
                      {board.high_card_rank ? (
                        <Chip size="small" label={`${board.high_card_rank}-high`} sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }} />
                      ) : null}
                      {board.paired_status ? (
                        <Chip size="small" label={board.paired_status} sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }} />
                      ) : null}
                      {board.suited_texture ? (
                        <Chip size="small" label={board.suited_texture} sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }} />
                      ) : null}
                      {board.connectivity_texture ? (
                        <Chip size="small" label={board.connectivity_texture} sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }} />
                      ) : null}
                      {formatBoardTextureLabel(board.broadway_density) ? (
                        <Chip size="small" label={formatBoardTextureLabel(board.broadway_density)} sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }} />
                      ) : null}
                      {board.low_board ? (
                        <Chip size="small" label="low" sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }} />
                      ) : null}
                      {board.board_tags.slice(0, 2).map((tag) => (
                        <Chip
                          key={tag}
                          size="small"
                          variant="outlined"
                          label={tag}
                          sx={{ height: 20, fontSize: 10.5, fontWeight: 850 }}
                        />
                      ))}
                    </Stack>

                    <Divider />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 10.5, fontWeight: 750 }}
                    >
                      Actualizado: {new Date(board.updated_at).toLocaleString()}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}


function buildSpotProfileSummary(spot: PokerStudySpotListItem): string {
  const profiles = spot.player_config?.profiles;

  if (!profiles || typeof profiles !== 'object') {
    return spot.villain_profile
      ? `Rival: ${formatPlayerProfileLabel(spot.villain_profile)}`
      : 'Sin perfiles definidos';
  }

  const typedProfiles = profiles as Record<string, unknown>;

  const positionAProfile =
    typeof typedProfiles[spot.hero_position] === 'string'
      ? formatPlayerProfileLabel(typedProfiles[spot.hero_position] as string)
      : null;

  const positionBProfile =
    typeof typedProfiles[spot.villain_position] === 'string'
      ? formatPlayerProfileLabel(typedProfiles[spot.villain_position] as string)
      : null;

  const parts = [
    positionAProfile ? `${spot.hero_position.toUpperCase()}: ${positionAProfile}` : null,
    positionBProfile ? `${spot.villain_position.toUpperCase()}: ${positionBProfile}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'Sin perfiles definidos';
}

export default function PokerStudyPage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const [activeUserEmail] = useState(() => readActiveSessionEmail());
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [passwordResetConfirmOpen, setPasswordResetConfirmOpen] = useState(false);
  const [isRequestingPasswordReset, setIsRequestingPasswordReset] = useState(false);
  const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);
  const [passwordResetErrorMessage, setPasswordResetErrorMessage] = useState<string | null>(null);
  const [spots, setSpots] = useState<PokerStudySpotListItem[]>([]);
  const [boards, setBoards] = useState<PokerStudyBoardListItem[]>([]);
  const [rangeTemplates, setRangeTemplates] = useState<PokerStudyPreflopRangeTemplate[]>([]);
  const [activeSection, setActiveSection] = useState<PokerStudyMainSection>('spots');
  const [isLoading, setIsLoading] = useState(true);
  const [isBoardsLoading, setIsBoardsLoading] = useState(false);
  const [boardsErrorMessage, setBoardsErrorMessage] = useState<string | null>(null);
  const [isRangeTemplatesLoading, setIsRangeTemplatesLoading] = useState(false);
  const [rangeTemplatesErrorMessage, setRangeTemplatesErrorMessage] = useState<string | null>(null);
  const [rangeTemplateDialogOpen, setRangeTemplateDialogOpen] = useState(false);
  const [rangeTemplateDialogMode, setRangeTemplateDialogMode] =
    useState<RangeTemplateDialogMode>('create');
  const [editingRangeTemplate, setEditingRangeTemplate] =
    useState<PokerStudyPreflopRangeTemplate | null>(null);
  const [rangeTemplatePendingDelete, setRangeTemplatePendingDelete] =
    useState<PokerStudyPreflopRangeTemplate | null>(null);
  const [rangeTemplateForm, setRangeTemplateForm] =
    useState<RangeTemplateFormState>(INITIAL_RANGE_TEMPLATE_FORM);
  const [isSubmittingRangeTemplate, setIsSubmittingRangeTemplate] = useState(false);
  const [isDeletingRangeTemplate, setIsDeletingRangeTemplate] = useState(false);
  const [editingRangeTemplateMatrix, setEditingRangeTemplateMatrix] =
    useState<PokerStudyPreflopRangeTemplate | null>(null);
  const [rangeTemplateDraft, setRangeTemplateDraft] = useState<Set<string>>(new Set());
  const [isRangeTemplatePainting, setIsRangeTemplatePainting] = useState(false);
  const [rangeTemplatePaintMode, setRangeTemplatePaintMode] =
    useState<'select' | 'clear' | null>(null);
  const [isSavingRangeTemplateMatrix, setIsSavingRangeTemplateMatrix] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [spotDialogMode, setSpotDialogMode] = useState<SpotDialogMode>('create');
  const [editingSpot, setEditingSpot] = useState<PokerStudySpotListItem | null>(null);
  const [spotPendingDelete, setSpotPendingDelete] = useState<PokerStudySpotListItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeletingSpot, setIsDeletingSpot] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [form, setForm] = useState<SpotFormState>(INITIAL_FORM);
  const [spotTypeFilter, setSpotTypeFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');

  const filteredSpots = useMemo(
    () =>
      spots.filter((spot) => {
        const matchesSpotType = spotTypeFilter === 'all' || spot.spot_type === spotTypeFilter;
        const matchesPosition =
          positionFilter === 'all' ||
          spot.hero_position === positionFilter ||
          spot.villain_position === positionFilter;

        return matchesSpotType && matchesPosition;
      }),
    [spots, spotTypeFilter, positionFilter],
  );

  const visibleBoards = useMemo(
    () => filteredSpots.reduce((acc, spot) => acc + (spot.boards_count || 0), 0),
    [filteredSpots],
  );

  const previewPreflopActors = useMemo(
    () => derivePreflopActors(form.spot_type, form.hero_position, form.villain_position),
    [form.hero_position, form.spot_type, form.villain_position],
  );

  const previewSpotTitle = useMemo(
    () => buildDefaultSpotTitle(form.spot_type, previewPreflopActors),
    [form.spot_type, previewPreflopActors],
  );

  function handlePokerStudyPasswordChangeRequest() {
    setPasswordResetMessage(null);
    setPasswordResetErrorMessage(null);
    setPasswordResetConfirmOpen(true);
  }

  async function confirmPokerStudyPasswordChangeRequest() {
    if (!activeUserEmail || activeUserEmail === 'Sesión activa') {
      setPasswordResetErrorMessage('No se pudo resolver el email de la sesión actual.');
      return;
    }

    setIsRequestingPasswordReset(true);
    setPasswordResetMessage(null);
    setPasswordResetErrorMessage(null);

    try {
      const { data } = await axiosInstance.post('/auth/request-password-reset', {
        email: activeUserEmail,
      });

      setPasswordResetMessage(
        data?.message || 'Te hemos enviado un enlace para cambiar la contraseña.',
      );
    } catch (error: any) {
      console.error(error);
      setPasswordResetErrorMessage(
        error.response?.data?.detail || 'No se pudo enviar el enlace de cambio de contraseña.',
      );
    } finally {
      setIsRequestingPasswordReset(false);
    }
  }

  async function handlePokerStudySignOut() {
    setSignOutConfirmOpen(true);
  }

  async function confirmPokerStudySignOut() {
    setSignOutConfirmOpen(false);
    await signOut();
    navigate('/poker/login', { replace: true });
  }


  async function loadSpots() {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const rows = await listPokerStudySpots();
      setSpots(rows);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudieron cargar los spots de estudio.');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadBoards() {
    setIsBoardsLoading(true);
    setBoardsErrorMessage(null);

    try {
      const rows = await listPokerStudyBoards();
      setBoards(rows);
    } catch (error) {
      console.error(error);
      setBoardsErrorMessage('No se pudieron cargar los boards de estudio.');
    } finally {
      setIsBoardsLoading(false);
    }
  }

  async function loadRangeTemplates() {
    setIsRangeTemplatesLoading(true);
    setRangeTemplatesErrorMessage(null);

    try {
      const rows = await listPokerStudyPreflopRangeTemplates();
      setRangeTemplates(rows);
    } catch (error) {
      console.error(error);
      setRangeTemplatesErrorMessage('No se pudieron cargar las plantillas de rangos.');
    } finally {
      setIsRangeTemplatesLoading(false);
    }
  }

  async function handleCopyRangeTemplate(
    template: PokerStudyPreflopRangeTemplate,
    payload: CopyPokerStudyPreflopRangeTemplatePayload,
  ) {
    setRangeTemplatesErrorMessage(null);

    try {
      await copyPokerStudyPreflopRangeTemplate(template.id, payload);
      await loadRangeTemplates();
    } catch (error) {
      console.error(error);
      setRangeTemplatesErrorMessage('No se pudo crear la copia personal del rango PROA.');
      throw error;
    }
  }

  function openCreateRangeTemplateDialog() {
    setRangeTemplateDialogMode('create');
    setEditingRangeTemplate(null);
    setRangeTemplateForm(INITIAL_RANGE_TEMPLATE_FORM);
    setRangeTemplatesErrorMessage(null);
    setRangeTemplateDialogOpen(true);
  }

  function openEditRangeTemplateDialog(template: PokerStudyPreflopRangeTemplate) {
    setRangeTemplateDialogMode('edit');
    setEditingRangeTemplate(template);
    setRangeTemplateForm(buildRangeTemplateFormFromTemplate(template));
    setRangeTemplatesErrorMessage(null);
    setRangeTemplateDialogOpen(true);
  }

  function closeRangeTemplateDialog() {
    if (isSubmittingRangeTemplate) return;
    setRangeTemplateDialogOpen(false);
    setEditingRangeTemplate(null);
    setRangeTemplateDialogMode('create');
  }

  async function handleSubmitRangeTemplate() {
    if (!rangeTemplateForm.name.trim()) {
      setRangeTemplatesErrorMessage('El nombre de la plantilla es obligatorio.');
      return;
    }

    setIsSubmittingRangeTemplate(true);
    setRangeTemplatesErrorMessage(null);

    try {
      const payload = buildRangeTemplatePayload(rangeTemplateForm);

      if (rangeTemplateDialogMode === 'edit' && editingRangeTemplate) {
        await updatePokerStudyPreflopRangeTemplate(editingRangeTemplate.id, {
          ...payload,
          range_state: editingRangeTemplate.range_state,
        });
      } else {
        await createPokerStudyPreflopRangeTemplate(payload);
      }

      setRangeTemplateDialogOpen(false);
      setEditingRangeTemplate(null);
      setRangeTemplateDialogMode('create');
      await loadRangeTemplates();
    } catch (error) {
      console.error(error);
      setRangeTemplatesErrorMessage(
        rangeTemplateDialogMode === 'edit'
          ? 'No se pudo actualizar la plantilla de rango.'
          : 'No se pudo crear la plantilla de rango.',
      );
    } finally {
      setIsSubmittingRangeTemplate(false);
    }
  }

  async function handleDeleteRangeTemplate() {
    if (!rangeTemplatePendingDelete) return;

    setIsDeletingRangeTemplate(true);
    setRangeTemplatesErrorMessage(null);

    try {
      await deletePokerStudyPreflopRangeTemplate(rangeTemplatePendingDelete.id);
      setRangeTemplatePendingDelete(null);
      await loadRangeTemplates();
    } catch (error) {
      console.error(error);
      setRangeTemplatesErrorMessage('No se pudo eliminar la plantilla de rango.');
    } finally {
      setIsDeletingRangeTemplate(false);
    }
  }

  function openRangeTemplateMatrixDialog(template: PokerStudyPreflopRangeTemplate) {
    setEditingRangeTemplateMatrix(template);
    setRangeTemplateDraft(new Set(getTemplateHandClasses(template)));
    setRangeTemplatesErrorMessage(null);
  }

  function closeRangeTemplateMatrixDialog() {
    if (isSavingRangeTemplateMatrix) return;

    setEditingRangeTemplateMatrix(null);
    setRangeTemplateDraft(new Set());
    setIsRangeTemplatePainting(false);
    setRangeTemplatePaintMode(null);
  }

  function applyRangeTemplatePaint(handClass: string, mode: 'select' | 'clear') {
    setRangeTemplateDraft((current) => {
      const next = new Set(current);

      if (mode === 'select') {
        next.add(handClass);
      } else {
        next.delete(handClass);
      }

      return next;
    });
  }

  function startRangeTemplatePaint(handClass: string) {
    if (editingRangeTemplateMatrix?.is_default) return;

    const mode = rangeTemplateDraft.has(handClass) ? 'clear' : 'select';

    setIsRangeTemplatePainting(true);
    setRangeTemplatePaintMode(mode);
    applyRangeTemplatePaint(handClass, mode);
  }

  function continueRangeTemplatePaint(handClass: string) {
    if (!isRangeTemplatePainting || !rangeTemplatePaintMode) {
      return;
    }

    applyRangeTemplatePaint(handClass, rangeTemplatePaintMode);
  }

  async function handleSaveRangeTemplateMatrix() {
    if (!editingRangeTemplateMatrix || editingRangeTemplateMatrix.is_default) return;

    setIsSavingRangeTemplateMatrix(true);
    setRangeTemplatesErrorMessage(null);

    try {
      const saved = await updatePokerStudyPreflopRangeTemplate(editingRangeTemplateMatrix.id, {
        name: editingRangeTemplateMatrix.name,
        range_family: editingRangeTemplateMatrix.range_family,
        position: editingRangeTemplateMatrix.position,
        versus_position: editingRangeTemplateMatrix.versus_position,
        spot_type: editingRangeTemplateMatrix.spot_type,
        description: editingRangeTemplateMatrix.description,
        tags: editingRangeTemplateMatrix.tags,
        range_state: {
          version: 1,
          hand_classes: sortRangeTemplateHandClasses(Array.from(rangeTemplateDraft)),
        },
      });

      setRangeTemplates((current) =>
        current.map((template) => (template.id === saved.id ? saved : template)),
      );
      closeRangeTemplateMatrixDialog();
    } catch (error) {
      console.error(error);
      setRangeTemplatesErrorMessage('No se pudo guardar el rango de la plantilla.');
    } finally {
      setIsSavingRangeTemplateMatrix(false);
    }
  }

  useEffect(() => {
    void loadSpots();
  }, []);

  useEffect(() => {
    function stopRangeTemplatePainting() {
      setIsRangeTemplatePainting(false);
      setRangeTemplatePaintMode(null);
    }

    window.addEventListener('mouseup', stopRangeTemplatePainting);

    return () => {
      window.removeEventListener('mouseup', stopRangeTemplatePainting);
    };
  }, []);

  useEffect(() => {
    if (activeSection !== 'boards') {
      return;
    }

    void loadBoards();
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'ranges') {
      return;
    }

    void loadRangeTemplates();
  }, [activeSection]);

  function openCreateDialog() {
    setSpotDialogMode('create');
    setEditingSpot(null);
    setForm(INITIAL_FORM);
    setErrorMessage(null);
    setIsCreateOpen(true);
  }

  function openEditDialog(spot: PokerStudySpotListItem) {
    setSpotDialogMode('edit');
    setEditingSpot(spot);
    setForm(buildFormFromSpot(spot));
    setErrorMessage(null);
    setIsCreateOpen(true);
  }

  function closeCreateDialog() {
    if (isCreating) return;
    setIsCreateOpen(false);
    setEditingSpot(null);
    setSpotDialogMode('create');
  }

  async function handleSubmitSpot() {
    if (form.hero_position === form.villain_position) {
      setErrorMessage('Las dos posiciones del spot deben ser diferentes.');
      return;
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const payload = buildCreatePayload(form);

      if (spotDialogMode === 'edit' && editingSpot) {
        await updatePokerStudySpot(editingSpot.id, payload);
        setIsCreateOpen(false);
        setEditingSpot(null);
        setSpotDialogMode('create');
        await loadSpots();
        return;
      }

      const created = await createPokerStudySpot(payload);
      setIsCreateOpen(false);
      await loadSpots();
      navigate(`/poker-study/spots/${created.id}`);
    } catch (error) {
      console.error(error);
      setErrorMessage(
        spotDialogMode === 'edit'
          ? 'No se pudo actualizar el spot.'
          : 'No se pudo crear el spot.',
      );
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDeleteSpot() {
    if (!spotPendingDelete) return;

    setIsDeletingSpot(true);
    setErrorMessage(null);

    try {
      await deletePokerStudySpot(spotPendingDelete.id);
      setSpotPendingDelete(null);
      await loadSpots();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo eliminar el spot.');
    } finally {
      setIsDeletingSpot(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#081120',
        color: '#f8fafc',
      }}
    >
      <PokerStudyTopNav
        activeSection={activeSection}
        activeUserEmail={activeUserEmail}
        onChangeSection={setActiveSection}
        onRequestPasswordChange={handlePokerStudyPasswordChangeRequest}
        onSignOut={handlePokerStudySignOut}
      />

      <Box sx={{ px: { xs: 2, md: 3, xl: 4 }, py: { xs: 2.5, md: 3 } }}>
        {errorMessage && !isCreateOpen ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      ) : null}

      {activeSection === 'spots' ? (
        <>

      <Card
        variant="outlined"
        sx={{
          mb: 2.5,
          borderRadius: 3,
          bgcolor: 'rgba(15, 23, 42, 0.18)',
          borderColor: 'rgba(148, 163, 184, 0.2)',
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                BIBLIOTECA DE SPOTS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Encuentra spots por tipo o posición, o crea una nueva línea de estudio.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              sx={{
                minWidth: { xs: '100%', md: 620 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.25,
                  bgcolor: 'rgba(255,255,255,0.035)',
                  transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.055)',
                    boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.16)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.18)',
                  },
                },
              }}
            >
              <TextField
                select
                size="small"
                label="Tipo de spot"
                value={spotTypeFilter}
                onChange={(event) => setSpotTypeFilter(event.target.value)}
                fullWidth
                sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}
              >
                <MenuItem value="all">Todos</MenuItem>
                {SPOT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Posición"
                value={positionFilter}
                onChange={(event) => setPositionFilter(event.target.value)}
                fullWidth
                sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}
              >
                <MenuItem value="all">Todas</MenuItem>
                {POSITION_OPTIONS.map((position) => (
                  <MenuItem key={position} value={position}>
                    {position.toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                onClick={openCreateDialog}
                sx={{
                  minHeight: 40,
                  minWidth: { xs: '100%', sm: 132 },
                  flexShrink: 0,
                  borderRadius: 2.25,
                  px: 2.4,
                  fontWeight: 950,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 14px 30px rgba(56,189,248,0.18)',
                }}
              >
                Nuevo spot
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Chip size="small" label={`${filteredSpots.length} spots visibles`} />
            <Chip size="small" label={`${visibleBoards} boards visibles`} />
            {(spotTypeFilter !== 'all' || positionFilter !== 'all') ? (
              <Chip
                size="small"
                variant="outlined"
                label="Limpiar filtros"
                onClick={() => {
                  setSpotTypeFilter('all');
                  setPositionFilter('all');
                }}
                sx={{ fontWeight: 850 }}
              />
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box
          sx={{
            minHeight: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : spots.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.2)',
            borderColor: 'rgba(148, 163, 184, 0.22)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={850}>
              Todavía no hay spots
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crea el primer spot para empezar a estructurar tu biblioteca de estudio.
            </Typography>
            <Button variant="contained" onClick={openCreateDialog}>
              Crear primer spot
            </Button>
          </CardContent>
        </Card>
      ) : filteredSpots.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            bgcolor: 'rgba(15, 23, 42, 0.2)',
            borderColor: 'rgba(148, 163, 184, 0.22)',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={850}>
              Ningún spot coincide con los filtros
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ajusta el tipo de spot o la posición para volver a mostrar resultados.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSpotTypeFilter('all');
                setPositionFilter('all');
              }}
            >
              Limpiar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredSpots.map((spot) => (
            <Grid item xs={12} md={6} lg={4} key={spot.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'rgba(15, 23, 42, 0.2)',
                  borderColor: 'rgba(148, 163, 184, 0.18)',
                  boxShadow: '0 14px 34px rgba(15, 23, 42, 0.12)',
                  transition:
                    'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    bgcolor: 'rgba(15, 23, 42, 0.32)',
                    borderColor: 'rgba(129, 140, 248, 0.52)',
                    boxShadow: '0 22px 48px rgba(15, 23, 42, 0.24)',
                  },
                }}
                onClick={() => navigate(`/poker-study/spots/${spot.id}`)}
              >
                <CardContent sx={{ p: 2.25 }}>
                  <Stack spacing={1.6}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={950}>
                          {formatSpotTypeLabel(spot.spot_type)}
                        </Typography>

                        <Typography variant="h6" fontWeight={900} noWrap>
                          {spot.title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                          {formatSpotSubtitle(spot)}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={getSpotTypeChipStyle(spot.spot_type).label}
                        variant="outlined"
                        sx={{
                          fontWeight: 950,
                          flexShrink: 0,
                          letterSpacing: 0.4,
                          bgcolor: getSpotTypeChipStyle(spot.spot_type).bgcolor,
                          color: getSpotTypeChipStyle(spot.spot_type).color,
                          borderColor: getSpotTypeChipStyle(spot.spot_type).borderColor,
                        }}
                      />
                    </Stack>

                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2.25,
                        bgcolor: 'rgba(255,255,255,0.035)',
                        borderColor: 'rgba(148, 163, 184, 0.14)',
                      }}
                    >
                      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                        <Stack spacing={0.55}>
                          <Typography variant="caption" color="text.secondary" fontWeight={950}>
                            SECUENCIA PREFLOP
                          </Typography>
                          <Typography variant="body2" fontWeight={850}>
                            {buildSpotSequenceLabel(spot)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {buildSpotProfileSummary(spot)}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip size="small" label={`${spot.boards_count} boards`} />
                      {spot.effective_stack_bb !== null && spot.effective_stack_bb !== undefined ? (
                        <Chip size="small" variant="outlined" label={`${spot.effective_stack_bb}bb`} />
                      ) : null}
                      {spot.general_tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} size="small" variant="outlined" label={tag} />
                      ))}
                    </Stack>

                    <Divider />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Actualizado: {new Date(spot.updated_at).toLocaleString()}
                      </Typography>

                      <Stack direction="row" spacing={0.75}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditDialog(spot);
                          }}
                          sx={{
                            minHeight: 26,
                            px: 1,
                            fontSize: 11,
                            fontWeight: 850,
                            borderRadius: 1.5,
                          }}
                        >
                          Editar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSpotPendingDelete(spot);
                          }}
                          sx={{
                            minHeight: 26,
                            px: 1,
                            fontSize: 11,
                            fontWeight: 850,
                            borderRadius: 1.5,
                          }}
                        >
                          Eliminar
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
        </>
      ) : activeSection === 'boards' ? (
        <PokerStudyBoardsSection
          boards={boards}
          isLoading={isBoardsLoading}
          errorMessage={boardsErrorMessage}
          onOpenBoard={(boardId) => navigate(`/poker-study/boards/${boardId}`)}
        />
      ) : (
        <PokerStudyRangesSection
          templates={rangeTemplates}
          isLoading={isRangeTemplatesLoading}
          errorMessage={rangeTemplatesErrorMessage}
          onCreateTemplate={openCreateRangeTemplateDialog}
          onEditTemplate={openEditRangeTemplateDialog}
          onEditTemplateRange={openRangeTemplateMatrixDialog}
          onCopyTemplate={handleCopyRangeTemplate}
          onDeleteTemplate={setRangeTemplatePendingDelete}
        />
      )}

      <Dialog
        open={passwordResetConfirmOpen}
        onClose={() => {
          if (!isRequestingPasswordReset) {
            setPasswordResetConfirmOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            CAMBIAR CONTRASEÑA
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            Enviar enlace de cambio
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enviaremos un enlace seguro al email de tu sesión actual para que puedas cambiar la contraseña.
            </Typography>

            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.035)',
                border: '1px solid rgba(148, 163, 184, 0.16)',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={950}
                sx={{ display: 'block' }}
              >
                EMAIL
              </Typography>
              <Typography variant="body2" fontWeight={850}>
                {activeUserEmail}
              </Typography>
            </Box>

            {passwordResetMessage ? (
              <Alert severity="success" variant="outlined">
                {passwordResetMessage}
              </Alert>
            ) : null}

            {passwordResetErrorMessage ? (
              <Alert severity="error" variant="outlined">
                {passwordResetErrorMessage}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPasswordResetConfirmOpen(false)}
            disabled={isRequestingPasswordReset}
          >
            {passwordResetMessage ? 'Cerrar' : 'Cancelar'}
          </Button>

          {!passwordResetMessage ? (
            <Button
              variant="contained"
              onClick={confirmPokerStudyPasswordChangeRequest}
              disabled={isRequestingPasswordReset}
              sx={{
                fontWeight: 900,
                borderRadius: 2,
                px: 2.4,
                whiteSpace: 'nowrap',
              }}
            >
              {isRequestingPasswordReset ? 'Enviando...' : 'Enviar enlace'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={signOutConfirmOpen}
        onClose={() => setSignOutConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            CERRAR SESIÓN
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            ¿Quieres salir de PokerStudy?
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Se cerrará tu sesión actual y volverás al login específico de PokerStudy.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSignOutConfirmOpen(false)}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={confirmPokerStudySignOut}
            sx={{
              fontWeight: 900,
              borderRadius: 2,
              px: 2.4,
              whiteSpace: 'nowrap',
            }}
          >
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingRangeTemplateMatrix)}
        onClose={closeRangeTemplateMatrixDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background:
              'linear-gradient(135deg, rgba(34,197,94,0.16), rgba(15,23,42,0.1) 55%, rgba(2,6,23,0.2))',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                {editingRangeTemplateMatrix?.is_default
                  ? 'RANGO PREDETERMINADO PROA'
                  : 'EDITOR DE RANGO PREFLOP'}
              </Typography>
              <Typography variant="h6" fontWeight={950} noWrap>
                {editingRangeTemplateMatrix?.name || 'Plantilla'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingRangeTemplateMatrix
                  ? `${formatRangeFamilyLabel(editingRangeTemplateMatrix.range_family)} · ${editingRangeTemplateMatrix.position.toUpperCase()}`
                  : ''}
              </Typography>
            </Box>

            <Chip
              size="small"
              label={`${getTemplateComboCount({
                ...(editingRangeTemplateMatrix || {}),
                range_state: {
                  version: 1,
                  hand_classes: Array.from(rangeTemplateDraft),
                },
              } as PokerStudyPreflopRangeTemplate)} combos · ${formatTemplatePercentage(
                getTemplateComboCount({
                  ...(editingRangeTemplateMatrix || {}),
                  range_state: {
                    version: 1,
                    hand_classes: Array.from(rangeTemplateDraft),
                  },
                } as PokerStudyPreflopRangeTemplate),
              )}`}
              sx={{
                fontWeight: 900,
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                border: '1px solid rgba(34, 197, 94, 0.32)',
                flexShrink: 0,
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'rgba(15, 23, 42, 0.18)' }}>
          <Stack spacing={1.5} sx={{ pt: 1.5 }}>
            {rangeTemplatesErrorMessage && editingRangeTemplateMatrix ? (
              <Alert severity="error">{rangeTemplatesErrorMessage}</Alert>
            ) : null}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(13, 42px)',
                gap: 0.45,
                justifyContent: 'center',
                p: 1.25,
                borderRadius: 3,
                bgcolor: 'rgba(15, 23, 42, 0.42)',
                border: '1px solid rgba(148, 163, 184, 0.16)',
                overflowX: 'auto',
              }}
            >
              {buildRangeTemplateMatrix().flat().map((handClass) => {
                const isSelected = rangeTemplateDraft.has(handClass);
                const isPair = isRangeTemplatePocketPair(handClass);

                return (
                  <Box
                    key={handClass}
                    onMouseDown={(event) => {
                      if (event.button !== 0) return;
                      event.preventDefault();
                      startRangeTemplatePaint(handClass);
                    }}
                    onMouseEnter={() => continueRangeTemplatePaint(handClass)}
                    sx={{
                      width: 42,
                      height: 34,
                      borderRadius: isPair ? 1.6 : 1.15,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: editingRangeTemplateMatrix?.is_default ? 'default' : 'pointer',
                      userSelect: 'none',
                      fontSize: 12,
                      fontWeight: isPair ? 1000 : 900,
                      color: isSelected ? '#052e16' : 'rgba(255,255,255,0.88)',
                      bgcolor: isSelected
                        ? '#22c55e'
                        : isPair
                          ? 'rgba(148, 163, 184, 0.12)'
                          : 'rgba(255,255,255,0.035)',
                      border: '1px solid',
                      borderColor: isSelected
                        ? 'rgba(134, 239, 172, 0.95)'
                        : isPair
                          ? 'rgba(226, 232, 240, 0.34)'
                          : 'rgba(148, 163, 184, 0.18)',
                      boxShadow: isSelected
                        ? '0 0 0 1px rgba(34, 197, 94, 0.22), 0 8px 18px rgba(34, 197, 94, 0.12)'
                        : isPair
                          ? 'inset 0 0 0 1px rgba(255,255,255,0.05)'
                          : 'none',
                      outline: isPair ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      transition:
                        'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
                      '&:hover': {
                        borderColor: isSelected ? '#bbf7d0' : 'primary.main',
                        bgcolor: isSelected ? '#4ade80' : 'rgba(76, 201, 216, 0.12)',
                      },
                    }}
                  >
                    {handClass}
                  </Box>
                );
              })}
            </Box>

            <Typography variant="caption" color="text.secondary" textAlign="center">
              {editingRangeTemplateMatrix?.is_default
                ? 'Rango predeterminado PROA en modo lectura. Crea una copia personal para adaptarlo.'
                : 'Click o arrastra para pintar. V1 guarda clases de mano completas, mostrando combos y porcentaje real.'}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.92)',
          }}
        >
          {!editingRangeTemplateMatrix?.is_default ? (
            <Button
              onClick={() => setRangeTemplateDraft(new Set())}
              disabled={isSavingRangeTemplateMatrix || rangeTemplateDraft.size === 0}
            >
              Limpiar
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={closeRangeTemplateMatrixDialog}
            disabled={isSavingRangeTemplateMatrix}
          >
            {editingRangeTemplateMatrix?.is_default ? 'Cerrar' : 'Cancelar'}
          </Button>

          {!editingRangeTemplateMatrix?.is_default ? (
            <Button
              variant="contained"
              onClick={handleSaveRangeTemplateMatrix}
              disabled={isSavingRangeTemplateMatrix || !editingRangeTemplateMatrix}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                px: 2.4,
                whiteSpace: 'nowrap',
                boxShadow: '0 12px 28px rgba(34, 197, 94, 0.18)',
              }}
            >
              {isSavingRangeTemplateMatrix ? 'Guardando...' : 'Guardar rango'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={rangeTemplateDialogOpen}
        onClose={closeRangeTemplateDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="text.secondary" fontWeight={950}>
            {rangeTemplateDialogMode === 'edit' ? 'EDITAR PLANTILLA' : 'NUEVA PLANTILLA'}
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {rangeTemplateForm.name.trim() || 'Plantilla de rango preflop'}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.25,
              bgcolor: 'rgba(255,255,255,0.035)',
            },
            '& .MuiInputLabel-root': {
              fontWeight: 850,
            },
          }}
        >
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            {rangeTemplatesErrorMessage ? (
              <Alert severity="error">{rangeTemplatesErrorMessage}</Alert>
            ) : null}

            <TextField
              label="Nombre"
              value={rangeTemplateForm.name}
              onChange={(event) =>
                setRangeTemplateForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              helperText="Ejemplo: OR CO, OR BTN, BB defend vs BTN"
            />

            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Familia"
                  value={rangeTemplateForm.range_family}
                  onChange={(event) =>
                    setRangeTemplateForm((current) => ({
                      ...current,
                      range_family: event.target.value,
                    }))
                  }
                  fullWidth
                >
                  {RANGE_FAMILY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Posición"
                  value={rangeTemplateForm.position}
                  onChange={(event) =>
                    setRangeTemplateForm((current) => ({
                      ...current,
                      position: event.target.value,
                    }))
                  }
                  fullWidth
                >
                  {POSITION_OPTIONS.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Contra posición"
                  value={rangeTemplateForm.versus_position}
                  onChange={(event) =>
                    setRangeTemplateForm((current) => ({
                      ...current,
                      versus_position: event.target.value,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="none">No aplica</MenuItem>
                  {POSITION_OPTIONS.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo de spot"
                  value={rangeTemplateForm.spot_type}
                  onChange={(event) =>
                    setRangeTemplateForm((current) => ({
                      ...current,
                      spot_type: event.target.value,
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="none">No aplica</MenuItem>
                  {SPOT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Perfil del rival"
                  value={rangeTemplateForm.opponent_profile}
                  onChange={(event) =>
                    setRangeTemplateForm((current) => ({
                      ...current,
                      opponent_profile: event.target.value,
                    }))
                  }
                  fullWidth
                >
                  {RANGE_TEMPLATE_OPPONENT_PROFILE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="Descripción"
              value={rangeTemplateForm.description}
              onChange={(event) =>
                setRangeTemplateForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Tags"
              value={rangeTemplateForm.tags_text}
              onChange={(event) =>
                setRangeTemplateForm((current) => ({
                  ...current,
                  tags_text: event.target.value,
                }))
              }
              fullWidth
              helperText="Separados por coma. Ejemplo: 50nl, pool, exploit"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeRangeTemplateDialog} disabled={isSubmittingRangeTemplate}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRangeTemplate}
            disabled={isSubmittingRangeTemplate}
            sx={{ fontWeight: 900 }}
          >
            {isSubmittingRangeTemplate
              ? 'Guardando...'
              : rangeTemplateDialogMode === 'edit'
                ? 'Guardar cambios'
                : 'Crear plantilla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(rangeTemplatePendingDelete)}
        onClose={() => {
          if (!isDeletingRangeTemplate) {
            setRangeTemplatePendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(248, 113, 113, 0.28)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="error" fontWeight={950}>
            ELIMINAR PLANTILLA
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {rangeTemplatePendingDelete?.name || 'Plantilla seleccionada'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción eliminará la plantilla reutilizable. No eliminará rangos ya importados en spots o boards.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Si confirmas, esta plantilla dejará de estar disponible para futuras importaciones.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setRangeTemplatePendingDelete(null)}
            disabled={isDeletingRangeTemplate}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteRangeTemplate}
            disabled={isDeletingRangeTemplate}
            sx={{ fontWeight: 900 }}
          >
            {isDeletingRangeTemplate ? 'Eliminando...' : 'Eliminar plantilla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(spotPendingDelete)}
        onClose={() => {
          if (!isDeletingSpot) {
            setSpotPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(248, 113, 113, 0.28)',
            boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="caption" color="error" fontWeight={950}>
            ELIMINAR SPOT
          </Typography>
          <Typography variant="h6" fontWeight={950}>
            {spotPendingDelete?.title || 'Spot seleccionado'}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción eliminará el spot y su contenido asociado. No continúes si solo querías corregir la configuración.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Si confirmas, se borrará este spot de la biblioteca.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setSpotPendingDelete(null)}
            disabled={isDeletingSpot}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteSpot}
            disabled={isDeletingSpot}
            sx={{ fontWeight: 900 }}
          >
            {isDeletingSpot ? 'Eliminando...' : 'Eliminar spot'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCreateOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background:
              'linear-gradient(135deg, rgba(99,102,241,0.22), rgba(15,23,42,0.12) 55%, rgba(2,6,23,0.2))',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                {spotDialogMode === 'edit' ? 'EDITAR SPOT' : 'NUEVO SPOT'}
              </Typography>
              <Typography variant="h6" fontWeight={950}>
                {previewSpotTitle}
              </Typography>
            </Box>

            <Chip
              color="primary"
              label={previewPreflopActors.sequenceLabel}
              sx={{ fontWeight: 900, maxWidth: 360 }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: 'rgba(15, 23, 42, 0.18)',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.25,
              bgcolor: 'rgba(255,255,255,0.035)',
              transition:
                'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.055)',
                boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.16)',
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.18)',
              },
            },
            '& .MuiInputLabel-root': {
              fontWeight: 800,
            },
          }}
        >
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                bgcolor: 'rgba(15, 23, 42, 0.22)',
                borderColor: 'rgba(148, 163, 184, 0.22)',
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack spacing={1.35}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={950}>
                        CONFIGURACIÓN PRINCIPAL
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Define el spot y los tamaños que construyen el bote preflop.
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={`${form.hero_position.toUpperCase()} vs ${form.villain_position.toUpperCase()}`}
                      color="primary"
                      sx={{ fontWeight: 900 }}
                    />
                  </Stack>

                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                      borderColor: 'rgba(129, 140, 248, 0.32)',
                    }}
                  >
                    <CardContent sx={{ p: 1.4, '&:last-child': { pb: 1.4 } }}>
                      <Stack spacing={0.6}>
                        <Typography variant="caption" color="text.secondary" fontWeight={950}>
                          TÍTULO AUTOMÁTICO
                        </Typography>
                        <Typography variant="h6" fontWeight={950}>
                          {previewSpotTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          El nombre se genera desde el tipo de spot y las posiciones, colocando delante la posición con iniciativa.
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Tipo de spot"
                        value={form.spot_type}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            spot_type: event.target.value,
                          }))
                        }
                        fullWidth
                      >
                        {SPOT_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Posición A"
                        value={form.hero_position}
                        onChange={(event) =>
                          setForm((current) => {
                            const nextPosition = event.target.value;

                            return {
                              ...current,
                              hero_position: nextPosition,
                              perspective_position:
                                current.perspective_position === current.hero_position
                                  ? nextPosition
                                  : current.perspective_position,
                            };
                          })
                        }
                        fullWidth
                      >
                        {POSITION_OPTIONS.map((position) => (
                          <MenuItem key={position} value={position}>
                            {position.toUpperCase()}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Posición B"
                        value={form.villain_position}
                        onChange={(event) =>
                          setForm((current) => {
                            const nextPosition = event.target.value;

                            return {
                              ...current,
                              villain_position: nextPosition,
                              perspective_position:
                                current.perspective_position === current.villain_position
                                  ? nextPosition
                                  : current.perspective_position,
                            };
                          })
                        }
                        fullWidth
                      >
                        {POSITION_OPTIONS.map((position) => (
                          <MenuItem key={position} value={position}>
                            {position.toUpperCase()}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {form.spot_type === 'srp' || form.spot_type === '3bet' || form.spot_type === '4bet' ? (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Open raise bb"
                          value={form.open_raise_size_bb}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              open_raise_size_bb: event.target.value,
                            }))
                          }
                          fullWidth
                        />
                      </Grid>
                    ) : null}

                    {form.spot_type === '3bet' || form.spot_type === '4bet' ? (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="3Bet bb"
                          value={form.three_bet_size_bb}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              three_bet_size_bb: event.target.value,
                            }))
                          }
                          fullWidth
                        />
                      </Grid>
                    ) : null}

                    {form.spot_type === '4bet' ? (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="4Bet bb"
                          value={form.four_bet_size_bb}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              four_bet_size_bb: event.target.value,
                            }))
                          }
                          fullWidth
                        />
                      </Grid>
                    ) : null}

                    {form.spot_type === 'rol' ? (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="ROL bb"
                          value={form.rol_size_bb}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              rol_size_bb: event.target.value,
                            }))
                          }
                          fullWidth
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            <Accordion
              disableGutters
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'rgba(148, 163, 184, 0.22)',
                bgcolor: 'rgba(15, 23, 42, 0.22)',
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<Box component="span">▾</Box>}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={950}>
                    CONFIGURACIÓN AVANZADA
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stack, perfiles, perspectiva, tipo de juego y etiquetas.
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Tipo de juego"
                      value={form.game_type}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, game_type: event.target.value }))
                      }
                      fullWidth
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="mtt">MTT</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Stack efectivo bb"
                      value={form.effective_stack_bb}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          effective_stack_bb: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Perspectiva de estudio"
                      value={form.perspective_position}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          perspective_position: event.target.value,
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="neutral">Neutral</MenuItem>
                      {[form.hero_position, form.villain_position].map((position) => (
                        <MenuItem key={position} value={position}>
                          {position.toUpperCase()}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label={`Perfil ${form.hero_position.toUpperCase()}`}
                      value={form.position_a_profile}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          position_a_profile: event.target.value,
                        }))
                      }
                      fullWidth
                    >
                      {PLAYER_PROFILE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label={`Perfil ${form.villain_position.toUpperCase()}`}
                      value={form.position_b_profile}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          position_b_profile: event.target.value,
                        }))
                      }
                      fullWidth
                    >
                      {PLAYER_PROFILE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Tags (separados por coma)"
                      value={form.general_tags_text}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          general_tags_text: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.6,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.92)',
          }}
        >
          <Button onClick={closeCreateDialog} disabled={isCreating}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitSpot}
            variant="contained"
            disabled={isCreating}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              px: 2.4,
              boxShadow: '0 12px 28px rgba(79, 70, 229, 0.28)',
            }}
          >
            {isCreating
              ? spotDialogMode === 'edit'
                ? 'Guardando...'
                : 'Creando...'
              : spotDialogMode === 'edit'
                ? 'Guardar cambios'
                : 'Crear spot'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}
